import { translationKey } from '../i18n';
import { getLocalizedLevelLabel } from '../components/character/stages';
import { localize } from '../i18n';
import useNavigate from '../hooks/useSafeNavigate';
import { sheetSpacing } from '../design/tokens';
import DialogTitle from '../components/ui/FitDialogTitle';
import SingleLineText from '../components/ui/SingleLineText';
import Button from '../components/ui/FitButton';
import Dialog from '../components/StableDialog';
import useRequestGuard from '../hooks/useRequestGuard';
import PageLoading from '../components/PageLoading';
import ConnectionRetryState from '../components/ConnectionRetryState';
import LoadingLabel from '../components/LoadingLabel';
import { isOral } from '../utils/intakeForm';
import React, { useState, useEffect, useLayoutEffect, useCallback, useRef } from 'react';
import useAlarmConfirmation from '../hooks/useAlarmConfirmation';
import useNativeAlarmSuccess from '../hooks/useNativeAlarmSuccess';
import { guardianOverdueMinutes } from '../utils/guardianLogTiming';
import { useLocation } from 'react-router-dom';
import { Box, Typography, Fab, DialogContent, DialogActions } from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import SkipNextIcon from '@mui/icons-material/SkipNext';
import AddIcon from '@mui/icons-material/Add';
import BlockIcon from '@mui/icons-material/Block';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import EventIcon from '@mui/icons-material/Event';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import MedicationIcon from '@mui/icons-material/Medication';
import SentimentSatisfiedAltIcon from '@mui/icons-material/SentimentSatisfiedAlt';
import EditNoteIcon from '@mui/icons-material/EditNote';

import RateReviewIcon from '@mui/icons-material/RateReview';
import FamilyRestroomIcon from '@mui/icons-material/FamilyRestroom';
import TouchAppIcon from '@mui/icons-material/TouchApp';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import WifiOffRoundedIcon from '@mui/icons-material/WifiOffRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import MedImage from '../components/MedImage';
import PageHeader from '../components/PageHeader';
import BottomSheet from '../components/BottomSheet';
import ScrollDownIndicator from '../components/ScrollDownIndicator';
import DoneGroupCard from '../components/DoneGroupCard';
import EditTakenTimeSheet from '../components/EditTakenTimeSheet';
import SnoozeSheet from '../components/SnoozeSheet';
import SkipSheet from '../components/SkipSheet';
import SkipReasonDetails from '../components/SkipReasonDetails';

import HomeDiscovery from '../components/HomeDiscovery';
import NotificationPermissionBanner from '../components/NotificationPermissionBanner';
import IosRingtonePermissionBanner from '../components/IosRingtonePermissionBanner';
import { FullScreenIntentPermissionBanner } from '../components/FullScreenIntentPermissionPrompt';
import HomeMenuList from '../components/HomeMenuList';
import PrnHomeCard from '../components/PrnHomeCard';
import PrnLogSheet from '../components/PrnLogSheet';
import ReviewSheet from '../components/ReviewSheet';
import StoreReviewSheet from '../components/StoreReviewSheet';
import AllDoneCelebration from '../components/AllDoneCelebration';
import AppToast from '../components/AppToast';
import CenterToast from '../components/CenterToast';
import AlarmIcon from '@mui/icons-material/Alarm';
import PetCharacter from '../components/character/PetCharacter';

import { getConfirmationWindow, getDeviceUuid, getToday, confirmLog, confirmLogs, unconfirmLog, unconfirmLogs, skipLog, skipLogs, deferLog, deferLogs, backfillLog, updateConfirmedAt, updateConfirmedAtLogs, getCalendar, getMedications, takeAsNeeded, deleteAsNeededLog, updateAsNeededLogTakenAt, getPrnLogsByDate, getGuardianTargets, getGuardianTargetToday, getMyGuardians, getStreak, getCharacter, getReviewUnreadCount } from '../api';
import { buildWidgetPayload, postWidgetUpdate, syncWidget, nextScheduledEntries } from '../utils/widgetSync';
import { hasCachedAdFreeEntitlement, requestAd } from '../utils/adRequests';
import {
    canShowStoreReviewPrompt,
    ensureStoreReviewFirstSeen,
    recordMedicationReviewEligibleAction,
    recordStoreReviewDismissed,
    recordStoreReviewShown,
    requestNativeStoreReview,
} from '../utils/storeReview';
import useMedicationAddGate from '../hooks/useMedicationAddGate';
import RegistrationDraftCard from '../components/RegistrationDraftCard';
import { useRegistrationDraft } from '../components/RegistrationDraftProvider';
import { clearMedicationCaches } from '../utils/localCache';
import { colors } from '../design/tokens';
import useAppLanguage from '../i18n';


function localDateKey(date = new Date()) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
}

function markInterstitialShownToday() {
    try {
        localStorage.setItem('interstitialLastShown', localDateKey());
    } catch {}
}

function isMedicationActiveOnDate(med, dateStr) {
    if (med.active === false) return false;
    if (med.paused || (med.pausedUntil && dateStr <= med.pausedUntil)
        || (med.globalPausedUntil && dateStr <= med.globalPausedUntil)) return false;
    if (med.frequencyType === 'AS_NEEDED') return false;
    if (med.startDate && dateStr < med.startDate) return false;
    if (med.endDate && dateStr > med.endDate) return false;
    if (med.frequencyType === 'SPECIFIC_DAYS' && med.specificDays) {
        const dayNames = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
        const [y, m, d] = dateStr.split('-').map(Number);
        return med.specificDays.includes(dayNames[new Date(y, m - 1, d).getDay()]);
    }
    if (med.frequencyType === 'EVERY_OTHER_DAY' && med.startDate) {
        const [sy, sm, sd] = med.startDate.split('-').map(Number);
        const [dy, dm, dd] = dateStr.split('-').map(Number);
        return Math.round((new Date(dy, dm - 1, dd) - new Date(sy, sm - 1, sd)) / 86400000) % 2 === 0;
    }
    if (med.frequencyType === 'EVERY_N_DAYS' && med.intervalDays && med.startDate) {
        const [sy, sm, sd] = med.startDate.split('-').map(Number);
        const [dy, dm, dd] = dateStr.split('-').map(Number);
        return Math.round((new Date(dy, dm - 1, dd) - new Date(sy, sm - 1, sd)) / 86400000) % med.intervalDays === 0;
    }
    if (med.frequencyType === 'CYCLE' && med.cycleDaysOn && med.cycleDaysOff && med.startDate) {
        const [sy, sm, sd] = med.startDate.split('-').map(Number);
        const [dy, dm, dd] = dateStr.split('-').map(Number);
        const diff = Math.round((new Date(dy, dm - 1, dd) - new Date(sy, sm - 1, sd)) / 86400000);
        return (diff % (med.cycleDaysOn + med.cycleDaysOff)) < med.cycleDaysOn;
    }
    if (med.frequencyType === 'MONTHLY' && med.monthlyDays) {
        return med.monthlyDays.split(',').map(x => parseInt(x)).includes(parseInt(dateStr.split('-')[2]));
    }
    return true;
}

function canBackfillOnPastDate(med, dateStr) {
    if (med.paused || med.active === false || !med.startDate) return false;
    if (med.endDate && dateStr > med.endDate) return false;
    if (med.frequencyType === 'DAILY') return dateStr <= med.startDate;
    if (med.frequencyType !== 'EVERY_N_DAYS' || !Number.isInteger(med.intervalDays) || med.intervalDays < 2 || dateStr < med.startDate) return false;
    const dayNumber = value => {
        const [year, month, day] = value.split('-').map(Number);
        return Date.UTC(year, month - 1, day) / 86400000;
    };
    return (dayNumber(dateStr) - dayNumber(med.startDate)) % med.intervalDays === 0;
}



function medActionLabels(intakeForm, t = value => value) {
    const oral = isOral(intakeForm);
    return {
        doneButton: t(oral ? '먹었어요' : '사용했어요'),
        donePast: t(oral ? '먹었어요' : '사용했어요'),
        prompt: t(oral ? '먹은 후에 눌러주세요' : '사용한 후에 눌러주세요'),
        later: t(oral ? translationKey('나중에 먹을게요') : '나중에 할게요'),
        completeTitle: t(oral ? '복용 완료' : '사용 완료'),
        backfillDone: t(oral ? '복용했어요' : '사용했어요'),
    };
}

function groupActionLabels(logs, t = value => value) {
    const forms = (logs || []).map(l => l.intakeForm);
    const allOral = forms.every(isOral);
    const allExternal = forms.length > 0 && forms.every(f => !isOral(f));
    if (allOral) return { doneButton: t('먹었어요'), later: t(translationKey('나중에 먹을게요')), completeTitle: t('복용 완료') };
    if (allExternal) return { doneButton: t('사용했어요'), later: t('나중에 할게요'), completeTitle: t('사용 완료') };
    return { doneButton: t('모두 완료'), later: t('나중에 할게요'), completeTitle: t('완료') };
}

// 백엔드 MedicationLogService.getCalendar의 status 판정 로직과 동일
// 옵티미스틱 weekData 업데이트에 사용
function computeDayStatus(logs) {
    if (!logs || logs.length === 0) return null;
    const taken = logs.filter(l => l.status === 'TAKEN').length;
    const skipped = logs.filter(l => l.status === 'SKIPPED').length;
    const missed = logs.filter(l => l.status === 'MISSED').length;
    const pending = logs.filter(l => l.status === 'PENDING').length;
    if (taken === logs.length) return 'ALL_TAKEN';
    if (missed > 0 || skipped > 0) return taken > 0 ? 'PARTIAL' : 'MISSED';
    if (taken > 0) return 'PARTIAL';
    if (pending > 0) return 'PENDING';
    return 'NONE';
}

const todayStr = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const dateFromStr = (dateStr) => {
    const [y, m, d] = dateStr.split('-').map(Number);
    return new Date(y, m - 1, d);
};

const formatDateStr = (date) => (
    `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
);

const addDaysToDateStr = (dateStr, days) => {
    const d = dateFromStr(dateStr);
    d.setDate(d.getDate() + days);
    return formatDateStr(d);
};

const getMiniWeekDates = (anchorDateStr) => {
    const anchor = dateFromStr(anchorDateStr);
    return Array.from({ length: 7 }, (_, idx) => {
        const d = new Date(anchor);
        d.setDate(anchor.getDate() + idx - 3);
        return formatDateStr(d);
    });
};

const getDateRange = (startDateStr, endDateStr) => {
    const dates = [];
    const d = dateFromStr(startDateStr);
    const end = dateFromStr(endDateStr);
    while (d <= end) {
        dates.push(formatDateStr(d));
        d.setDate(d.getDate() + 1);
    }
    return dates;
};

const getMonthKey = (dateStr) => dateStr.slice(0, 7);

const getMonthsForDates = (dateStrs) => Array.from(new Set(dateStrs.map(getMonthKey)));

const buildTodayWeekDataFromLogs = (logs, key = todayStr()) => {
    const todayLogs = (logs || []).filter(l =>
        l.frequencyType !== 'AS_NEEDED' && (!l.scheduledDate || l.scheduledDate === key)
    );
    const status = computeDayStatus(todayLogs);
    return status ? { [key]: status } : {};
};

const mergeCalendarLogs = (existing, incoming) => {
    const byKey = new Map();
    [...(existing || []), ...(incoming || [])].forEach((log) => {
        const key = log.id ?? `${log.scheduledDate}-${log.medicationId}-${log.scheduledTime}-${log.status}`;
        byKey.set(key, log);
    });
    return Array.from(byKey.values());
};

const isLowStock = (med) => med.totalStock && med.remainingStock != null && med.remainingStock <= med.stockAlertThreshold;

const getStockMessage = (med, t = value => value) => {
    if (med.remainingStock <= 0) return t('다 떨어졌어요');
    if (med.remainingStock === 1) return t('마지막 1정 남았어요');
    return t('{count}정 남았어요', { count: med.remainingStock });
};

const getStockStatusText = (meds, t = value => value) => {
    const outOfStockCount = meds.filter(m => isLowStock(m) && m.remainingStock <= 0).length;
    const lowStockCount = meds.filter(isLowStock).length;
    const stockManagedCount = meds.filter(m => m.totalStock).length;

    if (outOfStockCount > 0) return t('{count}개 약이 다 떨어졌어요', { count: outOfStockCount });
    if (lowStockCount > 0) return t('{count}개 약이 곧 떨어져요', { count: lowStockCount });
    return stockManagedCount > 0 ? t('{count}개 관리 중', { count: stockManagedCount }) : t('재고 알림 받기');
};

const compareMedicationLogs = (a, b) => {
    const timeCompare = (a.scheduledTime || '').localeCompare(b.scheduledTime || '');
    if (timeCompare !== 0) return timeCompare;

    const nameCompare = (a.medicationName || '').localeCompare(b.medicationName || '', 'ko');
    if (nameCompare !== 0) return nameCompare;

    const medicationIdCompare = Number(a.medicationId || 0) - Number(b.medicationId || 0);
    if (medicationIdCompare !== 0) return medicationIdCompare;

    return Number(a.id || 0) - Number(b.id || 0);
};

const triggerHaptic = (style = 'light') => {
    if (window.ReactNativeWebView) {
        window.ReactNativeWebView.postMessage(JSON.stringify({ type: 'HAPTIC', style }));
    }
};

const CHEER_MESSAGES = [
    translationKey('잘했어요! 오늘도 건강해지고 있어요 💪'),
    translationKey('완료! 꾸준함이 최고의 약이에요 ✨'),
    translationKey('좋아요! 오늘도 약속을 지켰어요 🎯'),
    translationKey('멋져요! 건강한 하루를 보내세요 🌿'),
    translationKey('완료! 작은 습관이 큰 변화를 만들어요 🌱'),
    translationKey('최고! 당신의 건강을 응원해요 💛'),
];

export default function HomePage() {
    const { language, t } = useAppLanguage();
    const CalendarDayText = language === 'ko' ? Typography : SingleLineText;
    const navigate = useNavigate();
    const location = useLocation();
    const { openMedicationAdd, MedicationAddGateDialog, checkingMedicationLimit } = useMedicationAddGate();
    const registrationDraft = useRegistrationDraft()?.draft;
    const draftCard = <RegistrationDraftCard
        onResume={() => openMedicationAdd('/medications/new-bundle?mode=manual', null, 'resume')}
        onNew={() => openMedicationAdd('/medications/new-bundle?mode=manual', null, 'new')} />;
    const cached = React.useMemo(() => {
        try {
            const raw = sessionStorage.getItem('home_cache');
            return raw ? JSON.parse(raw) : null;
        } catch { return null; }
    }, []);
    const initialTodayWeekData = React.useMemo(
        () => buildTodayWeekDataFromLogs(cached?.data?.logs),
        [cached?.data?.logs]
    );
    const [data, setData] = useState(cached?.data || null);
    const [character, setCharacter] = useState(cached?.character || null);
    const [petJumpTrigger, setPetJumpTrigger] = useState(0);
    const [evolveSparkle, setEvolveSparkle] = useState(false);
    const [flyAway, setFlyAway] = useState(false);
    const prevLevelRef = useRef(null);
    const flyAwayTimerRef = useRef(null);
    const jumpClickTimesRef = useRef([]);
    const snackbarLastShownRef = useRef(0);
    const [loading, setLoading] = useState(!cached?.data);
    const [skipSheet, setSkipSheet] = useState(null);
    const [skipReason, setSkipReason] = useState('');
    const [scrollIndicatorVisible, setScrollIndicatorVisible] = useState(false);
    // 다시 알림 시트 — 단일/일괄 통합 SnoozeSheet
    const [snoozeSheet, setSnoozeSheet] = useState({
        open: false, targetLogIds: [], targets: [], medName: null, medCount: 0,
    });
    // 일괄 처리 (그룹 카드)
    const [bulkSkipSheet, setBulkSkipSheet] = useState(null); // logIds[]
    const [medActionSheet, setMedActionSheet] = useState(null); // logId — 약 행 탭 시 단일 액션 메뉴
    const [hideMedRowHint, setHideMedRowHint] = useState(() => {
        try { return localStorage.getItem('bulk_med_row_hint_seen') === '1'; } catch { return false; }
    });
    const markMedRowHintSeen = () => {
        try { localStorage.setItem('bulk_med_row_hint_seen', '1'); } catch {}
        setHideMedRowHint(true);
    };
    // 복용 시간 수정 시트 — single(개별 약 행) / bulk(그룹 일괄) 통합
    const [timeEditSheet, setTimeEditSheet] = useState({
        open: false, mode: 'single', targets: [], initialTime: '',
        scheduledDate: '', scheduledTime: '', action: 'edit',
    });
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [centerToast, setCenterToast] = useState({ open: false, message: '', subMessage: '' });
    const [reviewOpen, setReviewOpen] = useState(false);
    const [reviewMode, setReviewMode] = useState('default');
    const [reviewSuccessOpen, setReviewSuccessOpen] = useState(false);
    const [unreadReviewReplies, setUnreadReviewReplies] = useState(0);
    const [storeReview, setStoreReview] = useState({ open: false, source: 'unknown' });

    // 건의 성공 토스트 — 2.5초 후 자동 닫기
    useEffect(() => {
        if (!reviewSuccessOpen) return;
        const t = setTimeout(() => setReviewSuccessOpen(false), 2500);
        return () => clearTimeout(t);
    }, [reviewSuccessOpen]);

    useEffect(() => {
        if (!centerToast.open) return;
        let timer;
        const update = () => {
            clearTimeout(timer);
            if (document.visibilityState !== 'hidden') {
                timer = setTimeout(() => setCenterToast(prev => ({ ...prev, open: false })), 1800);
            }
        };
        update();
        document.addEventListener('visibilitychange', update);
        return () => {
            clearTimeout(timer);
            document.removeEventListener('visibilitychange', update);
        };
    }, [centerToast]);

    const showCenterSuccess = useCallback((message, subMessage = '') => {
        setCenterToast({ open: true, message, subMessage });
    }, []);

    useEffect(() => {
        ensureStoreReviewFirstSeen();
    }, []);

    const refreshUnreadReviewReplies = useCallback(async () => {
        try {
            const result = await getReviewUnreadCount(getDeviceUuid());
            setUnreadReviewReplies(Number(result?.count || 0));
        } catch {
            setUnreadReviewReplies(0);
        }
    }, []);

    useEffect(() => {
        const handler = (event) => {
            try {
                const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
                if (data?.type !== 'OPEN_REVIEW_REPLY_SHEET') return;
                setReviewMode('reply');
                setReviewOpen(true);
                refreshUnreadReviewReplies();
            } catch {}
        };
        window.addEventListener('message', handler);
        document.addEventListener('message', handler);
        return () => {
            window.removeEventListener('message', handler);
            document.removeEventListener('message', handler);
        };
    }, [refreshUnreadReviewReplies]);

    useEffect(() => {
        postMedicationMountedRef.current = true;
        const handler = (event) => {
            try {
                const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
                if (data?.feature && data.feature !== 'medication_action') return;
                if (data?.type === 'INTERSTITIAL_AD_SHOW') {
                    setCenterToast(prev => ({ ...prev, open: false }));
                    markInterstitialShownToday();
                }
                if (['INTERSTITIAL_AD_UNAVAILABLE', 'INTERSTITIAL_AD_ERROR',
                    'INTERSTITIAL_AD_SKIPPED_AD_FREE', 'INTERSTITIAL_AD_CLOSED'].includes(data?.type)) {
                    interstitialRequestRef.current = false;
                }
            } catch {}
        };
        window.addEventListener('message', handler);
        document.addEventListener('message', handler);
        return () => {
            window.removeEventListener('message', handler);
            document.removeEventListener('message', handler);
            pendingPostCelebrationRef.current = null;
            postMedicationMountedRef.current = false;
        };
    }, []);

    const showInterstitialAd = useCallback(() => {
        if (!window.ReactNativeWebView || interstitialRequestRef.current) return false;
        const today = localDateKey();
        const lastShown = localStorage.getItem('interstitialLastShown');
        if (lastShown === today || hasCachedAdFreeEntitlement()) return false;
        interstitialRequestRef.current = true;
        markInterstitialShownToday();
        const requested = requestAd('interstitial', 'medication_action');
        if (!requested) interstitialRequestRef.current = false;
        return requested;
    }, []);

    const handlePostMedicationSuccess = useCallback((willCelebrate, actionCount, successMessage, successSubMessage = '') => {
        recordMedicationReviewEligibleAction(actionCount);
        // 복용 저장/버튼 잠금 해제는 리뷰·광고 네트워크 요청을 기다리지 않는다.
        if (!willCelebrate) showCenterSuccess(successMessage, successSubMessage);
        const run = async () => {
            if (!postMedicationMountedRef.current) return;
            const eligible = await canShowStoreReviewPrompt('medication_action');
            if (!postMedicationMountedRef.current) return;
            if (eligible) {
                setCenterToast(prev => ({ ...prev, open: false }));
                recordStoreReviewShown('medication_action');
                setStoreReview({ open: true, source: 'medication_action' });
                return;
            }
            showInterstitialAd();
        };
        const launch = () => { void run().catch(() => {}); };
        // API가 먼저 끝나면 축하 종료 시 실행, 축하가 먼저 끝나면 지금 실행.
        if (willCelebrate && showCelebrationRef.current) {
            pendingPostCelebrationRef.current = launch;
        } else {
            launch();
        }
        return true;
    }, [showCenterSuccess, showInterstitialAd]);

    const closeStoreReview = useCallback(() => {
        recordStoreReviewDismissed(storeReview.source);
        setStoreReview(prev => ({ ...prev, open: false }));
    }, [storeReview.source]);

    const handleStoreReviewRequest = useCallback(() => {
        requestNativeStoreReview(storeReview.source);
        setStoreReview(prev => ({ ...prev, open: false }));
    }, [storeReview.source]);
    const [weekData, setWeekData] = useState({ ...(cached?.weekData || {}), ...initialTodayWeekData });
    const [dateStripStart] = useState(() => addDaysToDateStr(todayStr(), -45));
    const [dateStripEnd] = useState(() => addDaysToDateStr(todayStr(), 3));
    const [selectedDate, setSelectedDate] = useState(null);
    const [futureMeds, setFutureMeds] = useState([]);
    const [selectedDateLogs, setSelectedDateLogs] = useState([]);
    const [calendarLogs, setCalendarLogs] = useState(cached?.calendarLogs || []);
    const [hasMedications, setHasMedications] = useState(cached?.hasMedications ?? true);
    const [allMeds, setAllMeds] = useState(cached?.allMeds || []);
    const [lowStockMeds, setLowStockMeds] = useState(cached?.lowStockMeds || []);
    const [familySummary, setFamilySummary] = useState(cached?.familySummary || null);
    const [processingIds, setProcessingIds] = useState(new Set());
    const [error, setError] = useState(false);
    const [showCelebration, setShowCelebration] = useState(false);
    // PRN 확장 카드용 — 오늘자 PRN 로그 + 기록 시트
    const [prnTodayLogs, setPrnTodayLogs] = useState([]);
    const [prnLogSheet, setPrnLogSheet] = useState({ open: false, mode: 'create', med: null, logId: null, date: null, time: null });
    const [prnDeleteConfirm, setPrnDeleteConfirm] = useState({ open: false, logId: null, medicationName: '' });
    const showCelebrationRef = useRef(false);
    const pendingPostCelebrationRef = useRef(null);
    const postMedicationMountedRef = useRef(true);
    const interstitialRequestRef = useRef(false);
    const beginRequest = useRequestGuard('home_cache');
    const beginPrnMutation = useRequestGuard('home-prn-mutation');
    const mutatingRef = React.useRef(0);
    const mutationVersionRef = useRef(0);
    const fetchVersionRef = useRef(0);
    // 날짜 색상 캐시는 오늘 한 날짜만 포함할 수 있으므로 월 전체 조회 완료의 근거가 아니다.
    const calendarLoadedMonthsRef = useRef(new Set());
    const calendarLoadingMonthsRef = useRef(new Map());
    const miniCalendarRef = useRef(null);
    const fetchInFlightRef = useRef(null);
    const lastFetchCompletedAtRef = useRef(0);
    const hasHomeDataRef = useRef(Boolean(cached?.data));
    const refreshFailureCountRef = useRef(0);
    const refreshRetryTimerRef = useRef(null);
    const familySummaryTimerRef = useRef(null);
    const widgetUpdateTimerRef = useRef(null);

    const prnMedIdSet = React.useMemo(
        () => new Set(allMeds.filter(m => m.frequencyType === 'AS_NEEDED').map(m => m.id)),
        [allMeds]
    );

    const logsForCalendarStatus = useCallback((logs) => (
        (logs || []).filter(l => l.frequencyType !== 'AS_NEEDED' && !prnMedIdSet.has(l.medicationId))
    ), [prnMedIdSet]);

    const updateTodayCalendarState = useCallback((logs) => {
        const key = todayStr();
        const todayLogs = logsForCalendarStatus(logs).filter(l => !l.scheduledDate || l.scheduledDate === key);
        const status = computeDayStatus(todayLogs);
        setWeekData(prev => (prev[key] === status ? prev : { ...prev, [key]: status }));
    }, [logsForCalendarStatus]);

    const scheduleWidgetUpdate = useCallback((result, meds) => {
        if (!window.ReactNativeWebView) return;
        if (widgetUpdateTimerRef.current) clearTimeout(widgetUpdateTimerRef.current);
        widgetUpdateTimerRef.current = setTimeout(() => {
            widgetUpdateTimerRef.current = null;
            postWidgetUpdate(buildWidgetPayload({ result, meds }));
        }, 150);
    }, []);

    const updateHomeLogs = useCallback((transformLogs, { updateWidget = true } = {}) => {
        mutationVersionRef.current++;
        if (!data?.logs) return;
        const updatedLogs = transformLogs(data.logs);
        updateTodayCalendarState(updatedLogs);
        setCalendarLogs(prevCalendarLogs => mergeCalendarLogs(prevCalendarLogs, updatedLogs));
        if (updateWidget) {
            scheduleWidgetUpdate({ ...data, logs: updatedLogs }, allMeds);
        }
        setData(prev => {
            if (!prev?.logs) return prev;
            const nextLogs = prev.logs === data.logs ? updatedLogs : transformLogs(prev.logs);
            return { ...prev, logs: nextLogs };
        });
    }, [allMeds, data, scheduleWidgetUpdate, updateTodayCalendarState]);

    const miniCalendarDates = React.useMemo(
        () => getDateRange(dateStripStart, dateStripEnd),
        [dateStripStart, dateStripEnd]
    );

    const loadCalendarMonths = useCallback(async (months) => {
        if (mutatingRef.current > 0) return;
        const uuid = getDeviceUuid();
        const targets = months.filter(month =>
            !calendarLoadedMonthsRef.current.has(month) && !calendarLoadingMonthsRef.current.get(month)?.()
        );
        if (targets.length === 0) return;
        const requestIsCurrent = beginRequest('months:' + targets.join(','));
        const mutationVersion = mutationVersionRef.current;
        const isCurrent = () => requestIsCurrent() && mutationVersion === mutationVersionRef.current;
        targets.forEach(month => calendarLoadingMonthsRef.current.set(month, isCurrent));
        try {
            const calendars = await Promise.all(targets.map(m => getCalendar(uuid, m)));
            if (!isCurrent()) return;
            const nextMap = {};
            let nextLogs = [];
            calendars.forEach(cal => {
                (cal?.days || []).forEach((day) => { nextMap[day.date] = day.status; });
                nextLogs = nextLogs.concat(cal?.logs || []);
            });
            setWeekData(prev => ({ ...prev, ...nextMap }));
            setCalendarLogs(prev => mergeCalendarLogs(prev, nextLogs));
            // 별도로 불러온 월도 보존해야 홈 재진입 때 과거 색상/기록이 비지 않는다.
            try {
                const prevCache = JSON.parse(sessionStorage.getItem('home_cache') || '{}');
                sessionStorage.setItem('home_cache', JSON.stringify({
                    ...prevCache,
                    weekData: { ...(prevCache?.weekData || {}), ...nextMap },
                    calendarLogs: mergeCalendarLogs(prevCache?.calendarLogs, nextLogs),
                }));
            } catch {}
            targets.forEach(month => calendarLoadedMonthsRef.current.add(month));
        } catch (e) {
            console.warn('Calendar load failed:', e);
        } finally {
            targets.forEach(month => {
                if (calendarLoadingMonthsRef.current.get(month) === isCurrent) calendarLoadingMonthsRef.current.delete(month);
            });
        }
    }, [beginRequest]);

    const resetHomeToToday = useCallback(() => {
        setSelectedDate(null);
        setFutureMeds([]);
        setSelectedDateLogs([]);
        requestAnimationFrame(() => {
            const el = miniCalendarRef.current;
            if (el) el.scrollLeft = el.scrollWidth - el.clientWidth;
        });
        window.scrollTo({ top: 0, behavior: 'auto' });
    }, []);

    const fetchData = useCallback(async (force = false, reconcile = false) => {
        if (!reconcile && mutatingRef.current > 0) return;
        if (!force && fetchInFlightRef.current) return fetchInFlightRef.current;
        if (!force && Date.now() - lastFetchCompletedAtRef.current < 5000) return;
        if (refreshRetryTimerRef.current) {
            clearTimeout(refreshRetryTimerRef.current);
            refreshRetryTimerRef.current = null;
        }
        const requestIsCurrent = beginRequest();
        const mutationVersion = mutationVersionRef.current;
        const fetchVersion = ++fetchVersionRef.current;
        const isCurrent = () => requestIsCurrent() && mutationVersion === mutationVersionRef.current
            && fetchVersion === fetchVersionRef.current
            && (reconcile || mutatingRef.current === 0);

        const run = (async () => {
            try {
                const uuid = getDeviceUuid();
                const [result, meds, char, prnToday, unreadReplies] = await Promise.all([
                    getToday(uuid),
                    getMedications(uuid),
                    getCharacter(uuid).catch(() => null),
                    getPrnLogsByDate(uuid).catch(() => ({ logs: [] })),
                    getReviewUnreadCount(uuid).catch(() => null),
                ]);
                // 응답 대기 중 사용자가 mutation 시작했으면 결과 무시 (옵티미스틱 업데이트 보호)
                if (!isCurrent()) return;
                setData(result);
                hasHomeDataRef.current = true;
                refreshFailureCountRef.current = 0;
                setError(false);
                const todayWeekData = buildTodayWeekDataFromLogs(result?.logs);
                if (Object.keys(todayWeekData).length > 0) {
                    setWeekData(prev => ({ ...prev, ...todayWeekData }));
                }
                if (char) setCharacter(char);
                if (unreadReplies) setUnreadReviewReplies(Number(unreadReplies?.count || 0));
                setHasMedications(meds && meds.length > 0);
                setAllMeds(meds || []);
                setPrnTodayLogs(prnToday?.logs || []);
                const lowStock = (meds || []).filter(isLowStock);
                setLowStockMeds(lowStock);

                // Core medication state is complete; calendar latency must not hide usable cards.
                setLoading(false);

                // 캘린더는 실패해도 메인 화면에 영향 없도록 분리
                // 미니 위크 캘린더가 ±3일 보여주므로 월초/월말엔 인접 달 데이터 필요
                const calendarTargets = getMonthsForDates(getMiniWeekDates(todayStr())).filter(month =>
                    !calendarLoadedMonthsRef.current.has(month) && !calendarLoadingMonthsRef.current.get(month)?.()
                );
                let calendarMap = {};
                let calendarLogsData = [];
                if (calendarTargets.length > 0) {
                    calendarTargets.forEach(month => calendarLoadingMonthsRef.current.set(month, isCurrent));
                    try {
                        const calendars = await Promise.all(calendarTargets.map(m => getCalendar(uuid, m)));
                        if (!isCurrent()) return;
                        calendars.forEach(cal => {
                            (cal?.days || []).forEach((day) => { calendarMap[day.date] = day.status; });
                            calendarLogsData = calendarLogsData.concat(cal?.logs || []);
                        });
                        calendarTargets.forEach(month => calendarLoadedMonthsRef.current.add(month));
                        setWeekData(prev => ({ ...prev, ...calendarMap }));
                        setCalendarLogs(prev => mergeCalendarLogs(prev, calendarLogsData));
                    } catch (e) {
                        console.warn('Calendar load failed:', e);
                    } finally {
                        calendarTargets.forEach(month => {
                            if (calendarLoadingMonthsRef.current.get(month) === isCurrent) calendarLoadingMonthsRef.current.delete(month);
                        });
                    }
                }

                // 캐시 저장은 calendar 성공 여부와 무관하게 수행
                if (!isCurrent()) return;
                // familySummary는 가족 fetch가 별도(fire-and-forget)로 채우므로 기존 값 보존
                let prevFamilySummary;
                let prevWeekData = {};
                let prevCalendarLogs = [];
                try {
                    const raw = sessionStorage.getItem('home_cache');
                    const prevCache = raw ? JSON.parse(raw) : {};
                    prevFamilySummary = prevCache?.familySummary;
                    prevWeekData = prevCache?.weekData || {};
                    prevCalendarLogs = prevCache?.calendarLogs || [];
                } catch { prevFamilySummary = undefined; }
                try {
                    sessionStorage.setItem('home_cache', JSON.stringify({
                        data: result, weekData: { ...prevWeekData, ...todayWeekData, ...calendarMap },
                        calendarLogs: mergeCalendarLogs(prevCalendarLogs, calendarLogsData),
                        hasMedications: meds && meds.length > 0, allMeds: meds || [],
                        lowStockMeds: lowStock,
                        familySummary: prevFamilySummary ?? null,
                        character: char ?? null,
                    }));
                } catch {}

                // 가족약알림 요약 — 첫 렌더 이후로 미뤄 앱 진입 직후 요청 폭주를 줄인다.
                if (familySummaryTimerRef.current) clearTimeout(familySummaryTimerRef.current);
                familySummaryTimerRef.current = setTimeout(async () => {
                    try {
                        const [targets, myGuardians] = await Promise.all([
                            getGuardianTargets(uuid).catch(() => []),
                            getMyGuardians(uuid).catch(() => []),
                        ]);
                        let next = null;
                        if ((!targets || targets.length === 0) && (!myGuardians || myGuardians.length === 0)) {
                            next = { state: 'A' };
                        } else {
                            const todays = await Promise.allSettled(
                                (targets || []).map(t => getGuardianTargetToday(uuid, t.groupId))
                            );
                            const issues = [];
                            todays.forEach((res, i) => {
                                if (res.status !== 'fulfilled') return;
                                const logs = res.value?.logs || [];
                                let missedCount = 0, veryOverdueCount = 0, worstOverdueMin = 0;
                                logs.forEach(log => {
                                    if (log.status === 'MISSED') {
                                        missedCount++;
                                    } else if (log.status === 'PENDING' && log.scheduledTime) {
                                        const overdueMin = guardianOverdueMinutes(log);
                                        if (overdueMin >= 30) {
                                            veryOverdueCount++;
                                            worstOverdueMin = Math.max(worstOverdueMin, overdueMin);
                                        }
                                    }
                                });
                                if (missedCount > 0 || veryOverdueCount > 0) {
                                    issues.push({ target: targets[i], missedCount, veryOverdueCount, worstOverdueMin });
                                }
                            });
                            if (issues.length > 0) {
                                issues.sort((a, b) => {
                                    if (b.missedCount !== a.missedCount) return b.missedCount - a.missedCount;
                                    return b.worstOverdueMin - a.worstOverdueMin;
                                });
                                const top = issues[0];
                                next = {
                                    state: 'B',
                                    primary: { nickname: top.target.patientNickname || translationKey('가족') },
                                    extraCount: issues.length - 1,
                                    route: '/guardian',
                                };
                            }
                            // issues 0건 → 연결 역할에 맞는 현황 페이지로 보낸다.
                            if (next === null) {
                                next = (targets || []).length > 0
                                    ? { state: 'C', route: '/guardian', sharingPaused: targets.some(t => t.deviceCount === 0) }
                                    : { state: 'W', route: '/my-guardians' };
                            }
                        }
                        if (!isCurrent()) return;
                        setFamilySummary(next);
                        try {
                            const raw = sessionStorage.getItem('home_cache');
                            const cur = raw ? JSON.parse(raw) : {};
                            sessionStorage.setItem('home_cache', JSON.stringify({ ...cur, familySummary: next }));
                        } catch {}
                    } catch (e) { console.warn('Family summary load failed:', e); }
                }, 900);

                // Update widget — 데이터 동기화만. widget_enabled 마킹은 위젯 탭 진입 신호로만 처리 (App.js의 WIDGET_OPENED 핸들러).
                if (window.ReactNativeWebView && result) {
                    postWidgetUpdate(buildWidgetPayload({ result, meds }));
                }
            } catch (err) {
                if (!isCurrent()) return;
                console.error('Failed to load today data:', err);
                if (document.visibilityState === 'hidden') {
                    refreshFailureCountRef.current = 0;
                } else if (hasHomeDataRef.current && refreshFailureCountRef.current++ === 0) {
                    // A suspended WebView request may fail just as the app resumes.
                    // Retry once before declaring the cached home data stale.
                    refreshRetryTimerRef.current = setTimeout(() => {
                        refreshRetryTimerRef.current = null;
                        if (document.visibilityState !== 'hidden') void fetchData(true);
                    }, 2000);
                } else {
                    setError(true);
                }
            } finally {
                if (requestIsCurrent()) {
                    lastFetchCompletedAtRef.current = Date.now();
                    setLoading(false);
                }
            }
        })();

        fetchInFlightRef.current = run;
        try {
            await run;
        } finally {
            if (fetchInFlightRef.current === run) fetchInFlightRef.current = null;
        }
    }, [beginRequest]);

    useEffect(() => {
        fetchData(true);
        const interval = setInterval(() => {
            if (document.visibilityState !== 'hidden') fetchData();
        }, 30000);
        const handleVisibility = () => {
            if (document.visibilityState === 'hidden') {
                refreshFailureCountRef.current = 0;
                if (refreshRetryTimerRef.current) {
                    clearTimeout(refreshRetryTimerRef.current);
                    refreshRetryTimerRef.current = null;
                }
            } else if (document.visibilityState === 'visible') {
                fetchData(true);
            }
        };
        document.addEventListener('visibilitychange', handleVisibility);
        const handleFocus = () => {
            if (document.visibilityState !== 'hidden') fetchData();
        };
        const handleOnline = () => fetchData(true);
        window.addEventListener('focus', handleFocus);
        window.addEventListener('online', handleOnline);
        window.addEventListener('native-app-foreground', handleOnline);
        return () => {
            clearInterval(interval);
            if (refreshRetryTimerRef.current) clearTimeout(refreshRetryTimerRef.current);
            if (familySummaryTimerRef.current) clearTimeout(familySummaryTimerRef.current);
            if (widgetUpdateTimerRef.current) clearTimeout(widgetUpdateTimerRef.current);
            document.removeEventListener('visibilitychange', handleVisibility);
            window.removeEventListener('focus', handleFocus);
            window.removeEventListener('online', handleOnline);
            window.removeEventListener('native-app-foreground', handleOnline);
        };
    }, [fetchData]);

    useEffect(() => {
        loadCalendarMonths(getMonthsForDates(miniCalendarDates));
    }, [loadCalendarMonths, miniCalendarDates, data?.logs, processingIds.size]);

    const resetMiniCalendarScroll = useCallback(() => {
        const el = miniCalendarRef.current;
        if (!el) return;
        const targetLeft = Math.max(0, el.scrollWidth - el.clientWidth);
        el.scrollLeft = targetLeft;
    }, []);

    const handleMiniCalendarRef = useCallback((node) => {
        miniCalendarRef.current = node;
        if (!node) return;

        let attempts = 0;
        const resetUntilSettled = () => {
            const el = miniCalendarRef.current;
            if (!el) return;
            const targetLeft = Math.max(0, el.scrollWidth - el.clientWidth);
            el.scrollLeft = targetLeft;

            attempts += 1;
            if (attempts < 12 && Math.abs(el.scrollLeft - targetLeft) > 1) {
                requestAnimationFrame(resetUntilSettled);
            }
        };

        resetUntilSettled();
        requestAnimationFrame(resetUntilSettled);
        setTimeout(resetUntilSettled, 0);
        setTimeout(resetUntilSettled, 80);
        setTimeout(resetUntilSettled, 240);
        setTimeout(resetUntilSettled, 600);
    }, []);

    const showMiniCalendar = hasMedications || calendarLogs.length > 0 || Object.keys(weekData).length > 0;

    useLayoutEffect(() => {
        if (!showMiniCalendar) return undefined;
        const frameId = requestAnimationFrame(resetMiniCalendarScroll);
        const timeoutId = setTimeout(resetMiniCalendarScroll, 120);
        resetMiniCalendarScroll();
        return () => {
            cancelAnimationFrame(frameId);
            clearTimeout(timeoutId);
        };
    }, [showMiniCalendar, resetMiniCalendarScroll, miniCalendarDates]);

    useEffect(() => {
        if (!showMiniCalendar) return undefined;
        const reset = () => requestAnimationFrame(resetMiniCalendarScroll);
        const handleVisibility = () => {
            if (document.visibilityState === 'visible') reset();
        };
        window.addEventListener('focus', reset);
        window.addEventListener('resize', reset);
        window.visualViewport?.addEventListener('resize', reset);
        document.addEventListener('visibilitychange', handleVisibility);
        return () => {
            window.removeEventListener('focus', reset);
            window.removeEventListener('resize', reset);
            window.visualViewport?.removeEventListener('resize', reset);
            document.removeEventListener('visibilitychange', handleVisibility);
        };
    }, [showMiniCalendar, resetMiniCalendarScroll]);

    useEffect(() => {
        if (!location.state?.refreshHome) return;
        fetchData(true);
        if (location.state?.toast) {
            setSnackbar({ open: true, message: location.state.toast, severity: 'success' });
        }
        navigate(location.pathname, { replace: true, state: {} });
    }, [fetchData, location.pathname, location.state?.refreshHome, location.state?.toast, navigate]);

    useEffect(() => {
        window.addEventListener('pill-reminder-home-tab', resetHomeToToday);
        return () => window.removeEventListener('pill-reminder-home-tab', resetHomeToToday);
    }, [resetHomeToToday]);

    // 오늘 logs 옵티미스틱 변경(복용/건너뛰기/취소 등) 시 미니 위크 캘린더에도 즉시 반영
    useEffect(() => {
        if (!data?.logs) return;
        updateTodayCalendarState(data.logs);
    }, [data?.logs, updateTodayCalendarState]);

    // 옵티미스틱 mutation은 logs만 갱신하고 streak는 prev 값을 유지하므로 헤더/폭죽 streak가 stale해짐.
    // 모든 mutation 응답이 도착해 processingIds가 비워지는 순간(=백엔드 streak 캐시도 evict 완료)에
    // streak를 재fetch — 응답 전에 fetch하면 옛 캐시값을 받아 stale 상태가 유지되므로
    // 반드시 응답 완료 시점을 기준으로 함.
    const prevProcessingSizeRef = useRef(0);
    useEffect(() => {
        const wasProcessing = prevProcessingSizeRef.current > 0;
        const isIdle = processingIds.size === 0;
        prevProcessingSizeRef.current = processingIds.size;
        if (!(wasProcessing && isIdle)) return undefined;
        const t = setTimeout(async () => {
            try {
                const uuid = getDeviceUuid();
                const [r, c] = await Promise.all([
                    getStreak(uuid).catch(() => null),
                    getCharacter(uuid).catch(() => null),
                ]);
                const newStreak = r?.streak;
                if (typeof newStreak === 'number') {
                    setData(prev => (prev && prev.streak !== newStreak) ? { ...prev, streak: newStreak } : prev);
                }
                if (c) setCharacter(c);
            } catch {}
        }, 100);
        return () => clearTimeout(t);
    }, [processingIds]);

    // 진화 감지 — character.level이 증가하면 sparkle + 점프 + 토스트
    useEffect(() => {
        if (!character) return;
        const prev = prevLevelRef.current;
        if (prev !== null && character.level > prev) {
            setPetJumpTrigger(t => t + 1);
            setEvolveSparkle(true);
            const t1 = setTimeout(() => setEvolveSparkle(false), 1500);
            // 받침에 따라 "이/가" 자동 선택
            const label = character.levelLabel || '';
            const last = label.charCodeAt(label.length - 1);
            const hasFinal = last >= 0xAC00 && last <= 0xD7A3
                ? (last - 0xAC00) % 28 !== 0
                : false;
            const particle = hasFinal ? translationKey('이') : translationKey('가');
            setSnackbar({
                open: true,
                message: language === 'ko'
                    ? localize("🎉 Lv.{v0} {v1}{v2} 됐어요!", { v0: character.level, v1: label, v2: particle })
                    : t('🎉 Lv.{level} · {name}!', { level: character.level, name: getLocalizedLevelLabel(character.level, character.levelLabel) }),
                severity: 'success',
            });
            return () => clearTimeout(t1);
        }
        prevLevelRef.current = character.level;
    }, [character?.level]);

    useEffect(() => () => {
        if (flyAwayTimerRef.current) clearTimeout(flyAwayTimerRef.current);
    }, []);

    const stopNativeAlarm = () => {
        if (window.ReactNativeWebView) window.ReactNativeWebView.postMessage(JSON.stringify({ type: 'STOP_ALARM' }));
    };


    const refreshStockData = async (uuid) => {
        const isCurrent = beginRequest('stock-data');
        try {
            const meds = await getMedications(uuid);
            if (!isCurrent() || getDeviceUuid() !== uuid) return;
            setAllMeds(meds);
            setLowStockMeds(meds.filter(isLowStock));
        } catch {} // A refresh failure must not turn a committed dose into a failed save.
    };

    const refreshPrnData = async (uuid) => {
        const isCurrent = beginRequest('prn-data');
        const [meds, records] = await Promise.all([
            getMedications(uuid).catch(() => null),
            getPrnLogsByDate(uuid).catch(() => null),
        ]);
        if (!isCurrent()) return;
        if (meds) { setAllMeds(meds); setLowStockMeds(meds.filter(isLowStock)); }
        if (records) setPrnTodayLogs(records.logs || []);
    };


    // PrnLogSheet onConfirm — HomePage PRN 확장 카드용. 시각 지정 기록 또는 사후 수정.
    const handlePrnSheetConfirm = async ({ date, time }) => {
        const { mode, med, logId } = prnLogSheet;
        const takenAt = `${date}T${time}:00`;
        mutatingRef.current++;
        clearMedicationCaches();
        const isCurrent = beginPrnMutation();
        try {
            const uuid = getDeviceUuid();
            if (mode === 'create') {
                await takeAsNeeded(uuid, med.id, takenAt);
                if (!isCurrent()) return;
                setSnackbar({ open: true, message: t('기록했어요'), severity: 'success' });
            } else {
                await updateAsNeededLogTakenAt(uuid, logId, takenAt);
                if (!isCurrent()) return;
                setSnackbar({ open: true, message: t('시간을 변경했어요'), severity: 'success' });
            }
            await refreshPrnData(uuid);
            if (isCurrent()) syncWidget(uuid);
        } catch (err) {
            if (!isCurrent()) return;
            if (err.prnRecovered) {
                await refreshPrnData(getDeviceUuid());
                if (!isCurrent()) return;
                syncWidget(getDeviceUuid());
            }
            const msg = err?.response?.data?.message || (mode === 'create' ? t('기록에 실패했어요') : t('변경에 실패했어요'));
            setSnackbar({ open: true, message: msg, severity: 'error' });
            throw err;
        } finally {
            mutatingRef.current--;
        }
    };

    // PRN 로그 삭제 — 휴지통 버튼 → 컨펌 모달 → 이 핸들러에서 실 삭제 처리.
    // open만 false로 — logId/medicationName은 Dialog transition 끝난 후 정리.
    const [prnDeleting, setPrnDeleting] = useState(false);
    const performPrnDelete = async () => {
        const { logId } = prnDeleteConfirm;
        if (!logId || prnDeleting) return;
        setPrnDeleting(true);
        clearMedicationCaches();
        const isCurrent = beginPrnMutation('delete');
        try {
            const uuid = getDeviceUuid();
            await deleteAsNeededLog(uuid, logId);
            if (!isCurrent()) return;
            setSnackbar({ open: true, message: t('기록을 삭제했어요'), severity: 'info' });
            setPrnDeleteConfirm(s => ({ ...s, open: false }));
            await refreshPrnData(uuid);
            if (isCurrent()) syncWidget(uuid);
        } catch {
            if (!isCurrent()) return;
            setSnackbar({ open: true, message: t('삭제에 실패했어요'), severity: 'error' });
        } finally { setPrnDeleting(false); }
    };

    // === 일괄 처리 핸들러 ===
    const handleConfirmGroup = async (logIds, fromAlarm = false, confirmedAt = null) => {
        const ids = logIds.filter(id => !processingIds.has(id));
        if (fromAlarm && ids.length !== logIds.length) throw new Error(t('복용 처리 중이에요. 잠시 후 다시 시도해주세요.'));
        if (ids.length === 0) return;
        mutatingRef.current++;
        clearMedicationCaches();
        setProcessingIds(prev => { const next = new Set(prev); ids.forEach(id => next.add(id)); return next; });
        // Native feedback must never prevent the server save or strand processing IDs.
        try { stopNativeAlarm(); triggerHaptic('success'); } catch {}
        const beforeLogs = data?.logs || [];
        const beforeById = new Map(beforeLogs.map(log => [log.id, log]));

        // 옵티미스틱 후 모두 TAKEN이 되는지 사전 계산 (setData functional updater는 render phase에서 실행되므로
        // 즉시 결과를 읽을 수 없음 → setShowCelebration이 같은 batch에 들어가도록 setData 전에 계산)
        let willCelebrate = (() => {
            if (fromAlarm) return false;
            if (!data?.logs) return false;
            const idSet = new Set(ids);
            const updated = data.logs.map(log => idSet.has(log.id) ? { ...log, status: 'TAKEN' } : log);
            const nonPrn = updated.filter(l => !prnMedIdSet.has(l.medicationId));
            return nonPrn.length > 0 && nonPrn.every(l => l.status === 'TAKEN');
        })();

        const nowIso = new Date().toISOString();
        if (!fromAlarm) updateHomeLogs(prevLogs => {
            const idSet = new Set(ids);
            return prevLogs.map(log =>
                idSet.has(log.id) ? { ...log, status: 'TAKEN', confirmedAt: nowIso } : log
            );
        });
        if (willCelebrate) showCelebrationOnce();

        let saved = false;
        try {
            const uuid = getDeviceUuid();
            const confirmed = confirmedAt ? await confirmLogs(uuid, ids, confirmedAt) : await confirmLogs(uuid, ids);
            const confirmedIds = new Set((confirmed || []).filter(log => log.status === 'TAKEN').map(log => log.id));
            if (confirmedIds.size !== ids.length || ids.some(id => !confirmedIds.has(id))) {
                throw new Error('Bulk confirm response did not include every requested log');
            }
            saved = true;
            void refreshStockData(uuid);
            updateHomeLogs(prevLogs => {
                const byId = new Map(confirmed.map(log => [log.id, log]));
                return prevLogs.map(log => byId.get(log.id) || log);
            });
            if (fromAlarm) {
                const byId = new Map(confirmed.map(log => [log.id, log]));
                const visible = beforeLogs.filter(log => !prnMedIdSet.has(log.medicationId));
                willCelebrate = visible.length > 0
                    && visible.some(log => log.status !== 'TAKEN' && byId.has(log.id))
                    && visible.every(log => (byId.get(log.id) || log).status === 'TAKEN');
                if (willCelebrate) showCelebrationOnce();
            }
            const cheer = CHEER_MESSAGES[Math.floor(Math.random() * CHEER_MESSAGES.length)];
            const confirmedLogs = beforeLogs.filter(log => ids.includes(log.id));
            const successMessage = fromAlarm ? t('복용 완료했어요') : groupActionLabels(confirmedLogs, t).completeTitle;
            const successSubMessage = t('{count}개 모두 {cheer}', { count: ids.length, cheer: t(cheer) });
            const handledPostSuccess = await handlePostMedicationSuccess(willCelebrate, ids.length, successMessage, successSubMessage);
            if (!handledPostSuccess) {
                showCenterSuccess(successMessage, successSubMessage);
            }
            if (fromAlarm) void fetchData(true, true);
        } catch (e) {
            if (saved) {
                // A toast/ad/widget error cannot turn a verified save into a retryable failure.
                console.warn('Post-confirmation UI failed:', e?.message);
                void fetchData(true, true);
                return;
            }
            if (fromAlarm) {
                void fetchData(true, true);
                const message = e?.response?.data?.message
                    || (e?.code === 'ECONNABORTED' ? t('서버 응답 시간이 초과됐어요. 복용 기록을 확인한 뒤 다시 시도해주세요.')
                        : e?.code === 'ERR_NETWORK' ? t('서버에 연결하지 못했어요. 연결 상태를 확인한 뒤 다시 시도해주세요.')
                        : e?.message || t('완료 여부를 확인하지 못했어요. 다시 시도해주세요.'));
                throw new Error(message);
            }
            console.error('confirmLogs failed:', e?.response?.status, e?.message);
            if (e?.response?.status === 400) {
                await fetchData(true, true);
                setSnackbar({ open: true, message: e?.response?.data?.message || t('이미 처리된 약이 있어요'), severity: 'info' });
            } else {
                if (willCelebrate) resetOptimisticCelebration();
                updateHomeLogs(prevLogs => {
                    const idSet = new Set(ids);
                    return prevLogs.map(log =>
                        idSet.has(log.id) ? (beforeById.get(log.id) || { ...log, status: 'PENDING', confirmedAt: null }) : log
                    );
                });
                await fetchData(true, true);
                setSnackbar({ open: true, message: t('연결이 불안정해요. 잠시 후 다시 시도해주세요'), severity: 'error' });
            }
        } finally {
            mutatingRef.current--;
            setProcessingIds(prev => { const next = new Set(prev); ids.forEach(id => next.delete(id)); return next; });
        }
    };
    const showNativeAlarmSuccess = useNativeAlarmSuccess(
        request => showCenterSuccess(t('복용 완료했어요'), t('{count}개 모두 저장했어요', { count: request.logIds.length })),
        !loading && !(error && !data),
    );
    const refreshedNativeSaves = useRef(new Set());
    const alarmConfirmation = useAlarmConfirmation(
        (ids, confirmedAt) => handleConfirmGroup(ids, true, confirmedAt),
        request => {
            showNativeAlarmSuccess(request);
            // FINISHED may be lost indefinitely. Refresh once for this receipt;
            // its replays are neither another dose nor a network reconnection.
            if (refreshedNativeSaves.current.has(request.requestId)) return;
            refreshedNativeSaves.current.add(request.requestId);
            clearMedicationCaches();
            void fetchData(true, true);
        },
    );

    const handleSkipGroup = async (logIds, reason) => {
        const ids = logIds.filter(id => !processingIds.has(id));
        if (ids.length === 0) return;
        mutatingRef.current++;
        clearMedicationCaches();
        setProcessingIds(prev => { const next = new Set(prev); ids.forEach(id => next.add(id)); return next; });
        stopNativeAlarm();
        triggerHaptic('light');

        const nowIso = new Date().toISOString();
        updateHomeLogs(prevLogs => {
            const idSet = new Set(ids);
            return prevLogs.map(log =>
                idSet.has(log.id) ? { ...log, status: 'SKIPPED', confirmedAt: nowIso, skipReason: reason } : log
            );
        });

        try {
            const uuid = getDeviceUuid();
            if (ids.length === 1) await skipLog(uuid, ids[0], reason);
            else await skipLogs(uuid, ids, reason);
            setSnackbar({ open: true, message: t('{count}개 건너뛰었어요', { count: ids.length }), severity: 'info' });
        } catch (e) {
            console.error('skipLogs failed:', e?.response?.status, e?.message);
            updateHomeLogs(prevLogs => {
                const idSet = new Set(ids);
                return prevLogs.map(log =>
                    idSet.has(log.id) ? { ...log, status: 'PENDING', confirmedAt: null, skipReason: null } : log
                );
            });
            setSnackbar({ open: true, message: e?.response?.data?.message || t('연결이 불안정해요. 잠시 후 다시 시도해주세요'), severity: 'error' });
            throw e;
        } finally {
            mutatingRef.current--;
            setProcessingIds(prev => { const next = new Set(prev); ids.forEach(id => next.delete(id)); return next; });
        }
    };

    const handleDeferGroup = async (logIds, deferUntil) => {
        const ids = logIds.filter(id => !processingIds.has(id));
        if (ids.length === 0) return;
        mutatingRef.current++;
        clearMedicationCaches();
        setProcessingIds(prev => { const next = new Set(prev); ids.forEach(id => next.add(id)); return next; });
        stopNativeAlarm();
        triggerHaptic('light');

        updateHomeLogs(prevLogs => {
            const idSet = new Set(ids);
            return prevLogs.map(log =>
                idSet.has(log.id) ? { ...log, deferredUntil: deferUntil } : log
            );
        });

        try {
            const uuid = getDeviceUuid();
            if (ids.length === 1) await deferLog(uuid, ids[0], deferUntil);
            else await deferLogs(uuid, ids, deferUntil);
            setSnackbar({ open: true, message: t('{count}개 모두 {time}에 다시 알려드릴게요', { count: ids.length, time: deferUntil.slice(11, 16) }), severity: 'info' });
        } catch (e) {
            console.error('deferLogs failed:', e?.response?.status, e?.message);
            updateHomeLogs(prevLogs => {
                const idSet = new Set(ids);
                return prevLogs.map(log =>
                    idSet.has(log.id) ? { ...log, deferredUntil: null } : log
                );
            });
            setSnackbar({ open: true, message: e?.response?.data?.message || t('연결이 불안정해요. 잠시 후 다시 시도해주세요'), severity: 'error' });
            throw e;
        } finally {
            mutatingRef.current--;
            setProcessingIds(prev => { const next = new Set(prev); ids.forEach(id => next.delete(id)); return next; });
        }
    };

    const handleConfirm = async (logId) => {
        if (processingIds.has(logId)) return;
        mutatingRef.current++;
        clearMedicationCaches();
        setProcessingIds(prev => new Set(prev).add(logId));
        stopNativeAlarm();
        triggerHaptic('success');

        // 옵티미스틱 후 모두 TAKEN이 되는지 사전 계산 (setData updater는 render phase에서 실행되므로
        // setShowCelebration이 같은 batch에 들어가도록 setData 전에 계산)
        const willCelebrate = (() => {
            if (!data?.logs) return false;
            const updated = data.logs.map(log => log.id === logId ? { ...log, status: 'TAKEN' } : log);
            const nonPrn = updated.filter(l => !prnMedIdSet.has(l.medicationId));
            return nonPrn.length > 0 && nonPrn.every(l => l.status === 'TAKEN');
        })();

        updateHomeLogs(prevLogs => (
            prevLogs.map(log =>
                log.id === logId ? { ...log, status: 'TAKEN', confirmedAt: new Date().toISOString() } : log
            )
        ));
        if (willCelebrate) showCelebrationOnce();

        try {
            const uuid = getDeviceUuid();
            await confirmLog(uuid, logId);
            void refreshStockData(uuid);
            const cheer = CHEER_MESSAGES[Math.floor(Math.random() * CHEER_MESSAGES.length)];
            const confirmedLog = data?.logs?.find(log => log.id === logId);
            const successMessage = medActionLabels(confirmedLog?.intakeForm, t).completeTitle;
            const handledPostSuccess = await handlePostMedicationSuccess(willCelebrate, 1, successMessage, cheer);
            if (!handledPostSuccess) {
                showCenterSuccess(successMessage, cheer);
            }
        } catch (e) {
            console.error('confirmLog failed:', e?.response?.status, e?.message);
            if (e?.response?.status === 400) {
                // 400은 이미 처리됨 외의 검증 오류도 포함하므로 서버 상태와 오류를 확인한다.
                await fetchData(true, true);
                setSnackbar({ open: true, message: e?.response?.data?.message || t('처리 상태를 확인하지 못했어요. 다시 확인해주세요.'), severity: 'info' });
            } else {
                if (willCelebrate) resetOptimisticCelebration();
                updateHomeLogs(prevLogs => (
                    prevLogs.map(log =>
                        log.id === logId ? { ...log, status: 'PENDING', confirmedAt: null } : log
                    )
                ));
                await fetchData(true, true);
                setSnackbar({ open: true, message: t('연결이 불안정해요. 잠시 후 다시 시도해주세요'), severity: 'error' });
            }
        } finally {
            mutatingRef.current--;
            setProcessingIds(prev => { const next = new Set(prev); next.delete(logId); return next; });
        }
    };

    const handleSkip = async () => {
        if (!skipSheet) return;
        const logId = skipSheet;
        if (processingIds.has(logId)) return;
        mutatingRef.current++;
        clearMedicationCaches();
        setProcessingIds(prev => new Set(prev).add(logId));
        const reason = skipReason || null;

        updateHomeLogs(prevLogs => (
            prevLogs.map(log =>
                log.id === logId ? { ...log, status: 'SKIPPED', confirmedAt: new Date().toISOString(), skipReason: reason } : log
            )
        ));
        stopNativeAlarm();
        triggerHaptic('success');

        try {
            const uuid = getDeviceUuid();
            await skipLog(uuid, logId, reason);
            setSkipSheet(null); setSkipReason('');
            setSnackbar({ open: true, message: t('건너뛰기 완료'), severity: 'info' });
            showInterstitialAd();
        } catch (e) {
            console.error('skipLog failed:', e?.response?.status, e?.message);
            if (e?.response?.status === 400) {
                await fetchData(true, true);
                setSkipSheet(null); setSkipReason('');
                setSnackbar({ open: true, message: e?.response?.data?.message || t('처리 상태를 확인하지 못했어요. 다시 확인해주세요.'), severity: 'info' });
            } else {
                updateHomeLogs(prevLogs => (
                    prevLogs.map(log =>
                        log.id === logId ? { ...log, status: 'PENDING', confirmedAt: null, skipReason: null } : log
                    )
                ));
                setSnackbar({ open: true, message: t('연결이 불안정해요. 잠시 후 다시 시도해주세요'), severity: 'error' });
            }
        } finally {
            mutatingRef.current--;
            setProcessingIds(prev => { const next = new Set(prev); next.delete(logId); return next; });
        }
    };

    const openSnoozeForLog = (log) => {
        setSnoozeSheet({
            open: true,
            targetLogIds: [log.id],
            medName: log.medicationName,
            medCount: 1,
            targets: [{ id: log.id, name: log.medicationName }],
        });
    };

    const openSnoozeForGroup = (logIds) => {
        const logs = data?.logs?.filter(log => logIds.includes(log.id)) || [];
        setSnoozeSheet({
            open: true,
            targetLogIds: logIds,
            medName: null,
            medCount: logIds.length,
            targets: logs.map(log => ({ id: log.id, name: log.medicationName })),
        });
    };

    const handleSnoozeConfirm = async (snoozeUntil) => {
        const ids = snoozeSheet.targetLogIds;
        if (!ids || ids.length === 0) return;
        const d = snoozeUntil;
        const pad2 = (n) => String(n).padStart(2, '0');
        const deferUntil = `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}T${pad2(d.getHours())}:${pad2(d.getMinutes())}:00`;
        await handleDeferGroup(ids, deferUntil);
    };

    const handleUnconfirm = async (logId) => {
        if (processingIds.has(logId)) return;
        const beforeLog = data?.logs?.find(log => log.id === logId);
        mutatingRef.current++;
        clearMedicationCaches();
        setProcessingIds(prev => new Set(prev).add(logId));

        updateHomeLogs(prevLogs => (
            prevLogs.map(log =>
                log.id === logId ? { ...log, status: 'PENDING', confirmedAt: null } : log
            )
        ));
        triggerHaptic('light');

        try {
            const uuid = getDeviceUuid();
            const updated = await unconfirmLog(uuid, logId);
            updateHomeLogs(logs => logs.map(log => log.id === logId ? updated : log));
            void refreshStockData(uuid);
            clearCelebrationShownForToday();
        } catch (e) {
            console.error('unconfirmLog failed:', e?.response?.status, e?.message);
            if (e?.response?.status === 400) {
                await fetchData(true, true);
                setSnackbar({ open: true, message: t('이미 변경된 상태예요'), severity: 'info' });
            } else {
                updateHomeLogs(prevLogs => (
                    prevLogs.map(log =>
                        log.id === logId && beforeLog ? beforeLog : log
                    )
                ));
                setSnackbar({ open: true, message: t('연결이 불안정해요. 잠시 후 다시 시도해주세요'), severity: 'error' });
            }
        } finally {
            mutatingRef.current--;
            setProcessingIds(prev => { const next = new Set(prev); next.delete(logId); return next; });
        }
    };

    const formatConfirmedTime = (iso) => {
        if (!iso) return null;
        try {
            const d = new Date(iso);
            if (Number.isNaN(d.getTime())) return null;
            return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
        } catch { return null; }
    };

    const openEditTime = (log) => {
        const t = formatConfirmedTime(log.confirmedAt) || log.scheduledTime?.slice(0, 5) || '12:00';
        setTimeEditSheet({
            open: true, mode: 'single',
            targets: [{ id: String(log.id), name: log.medicationName, takenAt: t }],
            initialTime: t,
            scheduledDate: log.scheduledDate,
            initialDate: log.confirmedAt?.slice(0, 10) || log.scheduledDate,
            scheduledTime: log.scheduledTime?.slice(0, 5) || t,
            action: 'edit',
        });
    };

    const openConfirmMissedTime = (log) => {
        const t = log.scheduledTime?.slice(0, 5) || '12:00';
        setTimeEditSheet({
            open: true, mode: 'single',
            targets: [{ id: String(log.id), name: log.medicationName, takenAt: t }],
            initialTime: t,
            scheduledDate: log.scheduledDate,
            initialDate: log.confirmedAt?.slice(0, 10) || log.scheduledDate,
            scheduledTime: t,
            action: 'confirm',
        });
    };

    const openEditTimeAll = (logs, groupTime) => {
        if (!logs || logs.length === 0) return;
        const meds = logs.map(l => ({
            id: String(l.id),
            name: l.medicationName,
            takenAt: formatConfirmedTime(l.confirmedAt) || groupTime,
        }));
        const latestConfirmedAt = logs.map(log => log.confirmedAt).filter(Boolean).sort().slice(-1)[0];
        const lastTakenAt = formatConfirmedTime(latestConfirmedAt) || groupTime;
        setTimeEditSheet({
            open: true, mode: 'bulk',
            targets: meds,
            initialTime: lastTakenAt,
            scheduledDate: logs[0].scheduledDate,
            initialDate: latestConfirmedAt?.slice(0, 10) || logs[0].scheduledDate,
            scheduledTime: groupTime,
            action: 'edit',
        });
    };

    const handleUnconfirmGroup = async (logIds) => {
        const ids = logIds.filter(id => !processingIds.has(id));
        if (ids.length === 0) return;
        mutatingRef.current++;
        clearMedicationCaches();
        setProcessingIds(prev => { const next = new Set(prev); ids.forEach(id => next.add(id)); return next; });

        const beforeLogs = data?.logs || [];
        const beforeById = new Map(beforeLogs.map(log => [log.id, log]));
        updateHomeLogs(prevLogs => {
            const idSet = new Set(ids);
            return prevLogs.map(log =>
                idSet.has(log.id) ? { ...log, status: 'PENDING', confirmedAt: null } : log
            );
        });
        triggerHaptic('light');

        try {
            const uuid = getDeviceUuid();
            const updated = ids.length === 1
                ? [await unconfirmLog(uuid, ids[0])]
                : await unconfirmLogs(uuid, ids);
            const byId = new Map(updated.map(log => [log.id, log]));
            updateHomeLogs(logs => logs.map(log => byId.get(log.id) || log));
            void refreshStockData(uuid);
            clearCelebrationShownForToday();
        } catch (e) {
            console.error('unconfirmLogs failed:', e?.response?.status, e?.message);
            updateHomeLogs(prevLogs => {
                const idSet = new Set(ids);
                return prevLogs.map(log =>
                    idSet.has(log.id) ? (beforeById.get(log.id) || { ...log, status: 'TAKEN', confirmedAt: new Date().toISOString() }) : log
                );
            });
            await fetchData(true, true);
            setSnackbar({ open: true, message: e?.response?.data?.message || t('되돌리기에 실패했어요'), severity: 'error' });
        } finally {
            mutatingRef.current--;
            setProcessingIds(prev => { const next = new Set(prev); ids.forEach(id => next.delete(id)); return next; });
        }
    };

    const handleApplyTime = async (logIds, scheduledDate, newTime, takenDate) => {
        if (!logIds || logIds.length === 0 || !scheduledDate || !newTime) return;
        clearMedicationCaches();
        const isoLocal = `${takenDate || scheduledDate}T${newTime}:00`;
        const idSet = new Set(logIds.map(String));

        // 옵티미스틱: 모든 대상 로그의 confirmedAt을 새 시간으로 갱신, 이전 값은 logId별로 캡처
        const prevByLog = new Map();
        const apply = (logs) => logs.map(log => {
            if (!idSet.has(String(log.id))) return log;
            if (!prevByLog.has(log.id)) prevByLog.set(log.id, log.confirmedAt);
            return { ...log, confirmedAt: isoLocal };
        });
        setData(prev => prev?.logs ? { ...prev, logs: apply(prev.logs) } : prev);
        setSelectedDateLogs(prev => apply(prev));
        setCalendarLogs(prev => apply(prev));

        try {
            const uuid = getDeviceUuid();
            if (logIds.length === 1) await updateConfirmedAt(uuid, logIds[0], isoLocal);
            else await updateConfirmedAtLogs(uuid, logIds, isoLocal);
            setSnackbar({
                open: true,
                message: logIds.length > 1 ? t('{count}개 약의 시각을 변경했어요', { count: logIds.length }) : t('복용 시각을 변경했어요'),
                severity: 'success',
            });
        } catch (e) {
            console.error('updateConfirmedAt failed:', e?.response?.status, e?.message);
            const rollback = (logs) => logs.map(log =>
                prevByLog.has(log.id) ? { ...log, confirmedAt: prevByLog.get(log.id) } : log
            );
            setData(prev => prev?.logs ? { ...prev, logs: rollback(prev.logs) } : prev);
            setSelectedDateLogs(prev => rollback(prev));
            setCalendarLogs(prev => rollback(prev));
            setSnackbar({ open: true, message: e?.response?.data?.message || t('시간 변경에 실패했어요'), severity: 'error' });
            throw e;
        }
    };

    const handleConfirmMissedAt = async (logIds, scheduledDate, newTime, takenDate) => {
        if (!logIds || logIds.length === 0 || !scheduledDate || !newTime) return;
        clearMedicationCaches();
        const isoLocal = `${takenDate || scheduledDate}T${newTime}:00`;
        const ids = logIds.map(Number);
        const idSet = new Set(ids);
        const beforeSelectedLogs = selectedDateLogs;
        const beforeCalendarLogs = calendarLogs;

        const apply = (logs) => logs.map(log =>
            idSet.has(Number(log.id)) ? { ...log, status: 'TAKEN', confirmedAt: isoLocal, skipReason: null } : log
        );
        const updatedSelectedLogs = apply(selectedDateLogs);
        const updatedCalendarLogs = apply(calendarLogs);
        setSelectedDateLogs(updatedSelectedLogs);
        setCalendarLogs(updatedCalendarLogs);
        setWeekData(prev => ({ ...prev, [scheduledDate]: computeDayStatus(updatedCalendarLogs.filter(l => l.scheduledDate === scheduledDate && l.frequencyType !== 'AS_NEEDED')) }));
        setProcessingIds(prev => {
            const next = new Set(prev);
            ids.forEach(id => next.add(id));
            return next;
        });

        try {
            const uuid = getDeviceUuid();
            if (ids.length === 1) await confirmLog(uuid, ids[0], isoLocal);
            else await confirmLogs(uuid, ids, isoLocal);
            void refreshStockData(uuid);
            setSnackbar({ open: true, message: localize('복용 기록을 남겼어요'), severity: 'success' });
        } catch (e) {
            console.error('confirm missed failed:', e?.response?.status, e?.message);
            setSelectedDateLogs(beforeSelectedLogs);
            setCalendarLogs(beforeCalendarLogs);
            setWeekData(prev => ({ ...prev, [scheduledDate]: computeDayStatus(beforeCalendarLogs.filter(l => l.scheduledDate === scheduledDate && l.frequencyType !== 'AS_NEEDED')) }));
            setSnackbar({ open: true, message: e?.response?.data?.message || localize('기록을 남기지 못했어요'), severity: 'error' });
            throw e;
        } finally {
            setProcessingIds(prev => {
                const next = new Set(prev);
                ids.forEach(id => next.delete(id));
                return next;
            });
        }
    };

    const handleBackfill = async (medId, status, dateOverride = null) => {
        const targetDate = dateOverride || selectedDate;
        if (!targetDate) return;
        const key = `backfill-${medId}`;
        if (processingIds.has(key)) return;
        clearMedicationCaches();
        setProcessingIds(prev => new Set(prev).add(key));
        try {
            const uuid = getDeviceUuid();
            const newLogs = await backfillLog(uuid, medId, targetDate, status);
            if (selectedDate) {
                setSelectedDateLogs(prev => {
                    const next = [...prev, ...newLogs];
                    // backfill한 날짜의 미니 캘린더 status 즉시 갱신
                    if (targetDate === selectedDate) {
                        setWeekData(wprev => ({ ...wprev, [targetDate]: computeDayStatus(next) }));
                    }
                    return next;
                });
                setCalendarLogs(prev => {
                    const next = [...prev, ...newLogs];
                    // selectedDate가 아닌 다른 날짜 backfill인 경우도 처리
                    if (targetDate !== selectedDate) {
                        const dateLogs = next.filter(l => l.scheduledDate === targetDate);
                        setWeekData(wprev => ({ ...wprev, [targetDate]: computeDayStatus(dateLogs) }));
                    }
                    return next;
                });
                if (targetDate === todayStr()) syncWidget(uuid);
            } else {
                await fetchData(true, true);
            }
            if (status === 'TAKEN') {
                showCenterSuccess(t('복용 기록을 남겼어요'));
            } else {
                setSnackbar({ open: true, message: t('건너뜀 기록을 남겼어요'), severity: 'success' });
            }
        } catch (e) {
            setSnackbar({ open: true, message: e.response?.data?.message || t('기록을 남기지 못했어요'), severity: 'error' });
        } finally {
            setProcessingIds(prev => { const next = new Set(prev); next.delete(key); return next; });
        }
    };

    const handleDateClick = useCallback(async (dateStr) => {
        const now = new Date();
        const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
        if (dateStr === today) { setSelectedDate(null); setFutureMeds([]); setSelectedDateLogs([]); return; }
        setSelectedDate(dateStr);

        const isPast = dateStr < today;
        if (isPast) {
            // 과거 날짜: 캘린더 로그에서 해당 날짜 로그 필터
            const dateLogs = calendarLogs.filter(l => l.scheduledDate === dateStr && l.frequencyType !== 'AS_NEEDED');
            setSelectedDateLogs(dateLogs);
            setFutureMeds([]);
        } else {
            // 미래 날짜: 활성 약 목록 표시 (캐시된 allMeds 우선 사용)
            setSelectedDateLogs([]);
            setFutureMeds([]);
            try {
                const meds = allMeds.length > 0 ? allMeds : await getMedications(getDeviceUuid());
                setFutureMeds(meds.filter(med => isMedicationActiveOnDate(med, dateStr)));
            } catch {}
        }
    }, [allMeds, calendarLogs]);

    useEffect(() => {
        if (!selectedDate || selectedDate >= todayStr()) return;
        setSelectedDateLogs(calendarLogs.filter(l => l.scheduledDate === selectedDate && l.frequencyType !== 'AS_NEEDED'));
    }, [calendarLogs, selectedDate]);

    const now = new Date();

    const sortedLogs = (data?.logs || [])
        .filter(l => !prnMedIdSet.has(l.medicationId))
        .filter(log => {
            if (log.status !== 'PENDING') return true;
            const med = allMeds.find(m => m.id === log.medicationId);
            return !med || ((!med.startDate || log.scheduledDate >= med.startDate)
                && (!med.endDate || log.scheduledDate <= med.endDate));
        })
        .slice()
        .sort((a, b) => {
            const order = { PENDING: 0, TAKEN: 1, SKIPPED: 2, MISSED: 3 };
            if (order[a.status] !== order[b.status]) return order[a.status] - order[b.status];
            return compareMedicationLogs(a, b);
        });

    // MISSED는 사용자가 능동적으로 처리한 상태가 아니므로(시간 지나 자동 전이) PENDING과 함께 액션 가능 영역에 둠.
    const actionableLogs = sortedLogs?.filter(l => l.status === 'PENDING' || l.status === 'MISSED') || [];
    const completedLogs = sortedLogs?.filter(l => l.status === 'TAKEN' || l.status === 'SKIPPED') || [];

    // 폭죽은 복용 처리에서만 실행한다. 조회/되돌리기 롤백은 완료 동작이 아니다.
    const getCelebrationTodayKey = useCallback(() => {
        const d = new Date();
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    }, []);

    const clearCelebrationShownForToday = useCallback(() => {
        try {
            if (localStorage.getItem('pr:celebrationShown') === getCelebrationTodayKey()) {
                localStorage.removeItem('pr:celebrationShown');
            }
        } catch {}
    }, [getCelebrationTodayKey]);

    const showCelebrationOnce = useCallback(() => {
        const today = getCelebrationTodayKey();
        const key = 'pr:celebrationShown';
        let alreadyShown = false;
        try { alreadyShown = localStorage.getItem(key) === today; } catch {}
        if (alreadyShown) return;
        showCelebrationRef.current = true;
        setShowCelebration(true);
        try { localStorage.setItem(key, today); } catch {}
    }, [getCelebrationTodayKey]);

    const resetOptimisticCelebration = useCallback(() => {
        showCelebrationRef.current = false;
        pendingPostCelebrationRef.current = null;
        setShowCelebration(false);
        try { localStorage.removeItem('pr:celebrationShown'); } catch {}
    }, []);

    // legacy cleanup: 과거 날짜별로 쌓인 'pr:celebrationShown:YYYY-MM-DD' 키 일괄 제거 (1회)
    useEffect(() => {
        try {
            const stale = [];
            for (let i = 0; i < localStorage.length; i++) {
                const k = localStorage.key(i);
                if (k && k !== 'pr:celebrationShown' && k.startsWith('pr:celebrationShown:')) {
                    stale.push(k);
                }
            }
            stale.forEach(k => localStorage.removeItem(k));
        } catch {}
    }, []);

    // 같은 시각 약 그룹화 (effective time + deferred 상태 동일하면 한 그룹)
    const actionableGroups = React.useMemo(() => {
        const groups = new Map();
        for (const log of actionableLogs) {
            const isDeferred = !!(log.deferredUntil && new Date(log.deferredUntil) > new Date());
            const effTime = isDeferred ? log.deferredUntil.slice(11, 16) : log.scheduledTime.slice(0, 5);
            const date = log.scheduledDate || todayStr();
            const key = `${date}|${effTime}|${isDeferred ? 'def' : 'sch'}`;
            if (!groups.has(key)) groups.set(key, { key, date, time: effTime, isDeferred, logs: [] });
            groups.get(key).logs.push(log);
        }
        return Array.from(groups.values()).sort((a, b) => a.date.localeCompare(b.date) || a.time.localeCompare(b.time));
    }, [actionableLogs]);

    // 완료 섹션: 약별 완료 시각으로 먼저 정렬하고, 이어진 같은 분의 복용 완료만 묶는다.
    const completedItems = React.useMemo(() => {
        const completedAt = log => new Date(log.confirmedAt || `${log.scheduledDate}T${log.scheduledTime}`).getTime() || 0;
        const byRecentCompletion = completedLogs.slice().sort((a, b) =>
            completedAt(b) - completedAt(a) || (b.scheduledTime || '').localeCompare(a.scheduledTime || '')
        );
        const items = [];
        for (const log of byRecentCompletion) {
            const time = log.scheduledTime?.slice(0, 5) || '';
            if (log.status === 'TAKEN') {
                const minute = (log.confirmedAt || `${log.scheduledDate}T${log.scheduledTime}`).slice(0, 16);
                const previous = items[items.length - 1];
                if (previous?.type === 'taken-group' && previous.date === log.scheduledDate
                    && previous.time === time && previous.minute === minute) {
                    previous.logs.push(log);
                } else {
                    items.push({ type: 'taken-group', date: log.scheduledDate, time, minute, logs: [log] });
                }
            } else {
                items.push({ type: 'single', time, log });
            }
        }
        return items;
    }, [completedLogs]);

    if (loading) {
        return (
            <PageLoading />
        );
    }

    if (error && !loading && !data) {
        return (
            <Box sx={{ pb: 10 }}>
                <PageHeader title={t('오늘의 약')} />
                <ConnectionRetryState onRetry={() => fetchData(true)} />
            </Box>
        );
    }

    return (
        <Box sx={{ pb: 10 }}>
            <AllDoneCelebration
                open={showCelebration}
                streak={data?.streak}
                onClose={() => {
                    showCelebrationRef.current = false;
                    setShowCelebration(false);
                    const pending = pendingPostCelebrationRef.current;
                    pendingPostCelebrationRef.current = null;
                    pending?.();
                }}
            />
            {/* Header */}
            <PageHeader
                title={localize("오늘의 약")}
                matchContentHeight
                rightAction={character ? (
                    <Box
                        role="button"
                        tabIndex={0}
                        onClick={() => {
                            if (flyAway) return;
                            const now = Date.now();
                            jumpClickTimesRef.current.push(now);
                            jumpClickTimesRef.current = jumpClickTimesRef.current.filter(t => now - t < 1200);

                            // 짧은 시간 내 3번 연속 클릭 → 화면 밖으로 날아감 (안 돌아옴 → 4.5s 후 자연 복귀)
                            if (jumpClickTimesRef.current.length >= 3) {
                                setFlyAway(true);
                                if (flyAwayTimerRef.current) clearTimeout(flyAwayTimerRef.current);
                                flyAwayTimerRef.current = setTimeout(() => setFlyAway(false), 4500);
                                jumpClickTimesRef.current = [];
                                return;
                            }

                            setPetJumpTrigger(t => t + 1);

                            // 스낵바 cooldown 4초 — 연속 클릭으로 매번 뜨지 않게
                            if (now - snackbarLastShownRef.current < 4000) return;
                            snackbarLastShownRef.current = now;

                            const cur = character.currentLevelXp || 0;
                            const tot = character.xpForNextLevel || 0;
                            const lvLabel = `Lv.${character.level} ${getLocalizedLevelLabel(character.level, character.levelLabel)}`;
                            const main = !character.nextLevelLabel
                                ? t('{level} 도달! 최고예요{streak}', { level: lvLabel, streak: character.streak > 0 ? t(' · 연속 {count}일', { count: character.streak }) : '' })
                                : (character.level === 0
                                    ? t('약을 챙기면 알이 생겨요')
                                    : t('{level} · {current} / {total}점{streak}', { level: lvLabel, current: cur, total: tot, streak: character.streak > 0 ? t(' · 연속 {count}일', { count: character.streak }) : '' }));
                            const tip = character.level === 0
                                ? ''
                                : t('\n연속 복용하면 더 많은 점수를 얻을 수 있어요!');
                            setSnackbar({ open: true, message: main + tip, severity: 'success' });
                        }}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            height: 38,
                            ...(language === 'ko' ? {} : { maxWidth: '54%', minWidth: 0 }),
                            cursor: 'pointer',
                            '&:active': { opacity: 0.7 },
                        }}
                    >
                        <Box sx={{ width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, overflow: 'visible' }}>
                            <PetCharacter
                                level={character.level}
                                size={36}
                                jumpTrigger={petJumpTrigger}
                                evolving={evolveSparkle}
                                flyAway={flyAway}
                            />
                        </Box>
                        <Box sx={{ minWidth: 0, whiteSpace: 'nowrap' }}>
                            <Typography data-character-stage-name sx={{ fontSize: '0.6rem', fontWeight: 600, color: '#9a9a9a', lineHeight: 1 }}>
                                Lv.{character.level} {getLocalizedLevelLabel(character.level, character.levelLabel)}
                            </Typography>
                            {character.streak > 0 && (
                                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.2, lineHeight: 1, mt: 0.4 }}>
                                    <Typography component="span" sx={{ fontSize: '0.9rem', fontWeight: 800, color: '#f59e0b', lineHeight: 1 }}>
                                        {character.streak}
                                    </Typography>
                                    <Typography component="span" sx={{ fontSize: '0.6rem', fontWeight: 600, color: '#666', lineHeight: 1 }}>
                                        {t('일 연속 복용')}
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                    </Box>
                ) : null}
            />

            {error && (
                <Box sx={{ px: 2, pt: 1.5 }}>
                    <Box role="status" sx={{
                        p: 2, borderRadius: '18px', bgcolor: colors.surface,
                        border: `1px solid ${colors.borderNeutral}`,
                        boxShadow: '0 5px 18px rgba(26, 26, 26, 0.05)',
                    }}>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                            <Box sx={{
                                width: 40, height: 40, flexShrink: 0, borderRadius: '13px',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                bgcolor: '#fff5cf', color: colors.brandDark,
                            }}>
                                <WifiOffRoundedIcon sx={{ fontSize: 23 }} />
                            </Box>
                            <Box sx={{ minWidth: 0, flex: 1 }}>
                                <SingleLineText sx={{ fontSize: '0.94rem', fontWeight: 800, color: colors.text, lineHeight: 1.4 }}>{t('최신 내용을 불러오지 못했어요')}</SingleLineText>
                                <SingleLineText sx={{ mt: 0.35, fontSize: '0.81rem', color: colors.textSecondary, lineHeight: 1.5 }}>{t('이전 내용을 계속 볼 수 있어요.')}</SingleLineText>
                            </Box>
                        </Box>
                        <Button fullWidth onClick={() => fetchData(true)} startIcon={<RefreshRoundedIcon sx={{ fontSize: 18 }} />}
                            sx={{ mt: 1.75, minHeight: 42, borderRadius: '11px', bgcolor: '#fff8df',
                                color: colors.brandDark, fontWeight: 800, fontSize: '0.84rem',
                                boxShadow: 'none', '&:hover': { bgcolor: '#fff0b9', boxShadow: 'none' } }}>{t('다시 시도')}</Button>
                    </Box>
                </Box>
            )}

            <Box sx={{ pt: 1.5, display: 'flex', flexDirection: 'column', gap: 1.5,
                '& > [role="status"]': { mx: 2, mb: 0 } }}>
                <NotificationPermissionBanner needed={allMeds.some(med => med.frequencyType !== 'AS_NEEDED' && med.active !== false)
                    || ['B', 'C', 'W', 'I'].includes(familySummary?.state)} channelIds={[
                        'default',
                        ...(allMeds.some(med => med.alertType === 'FULLSCREEN' && med.frequencyType !== 'AS_NEEDED' && med.active !== false) ? ['pill-reminder-alarm', 'pill-alarm-foreground-v2'] : []),
                        ...(allMeds.some(med => med.alertType === 'VIBRATION' && med.frequencyType !== 'AS_NEEDED' && med.active !== false) ? ['pill-reminder-vibration', 'pill-alarm-foreground-v2'] : []),
                    ]} />
                <FullScreenIntentPermissionBanner medications={allMeds} source="home_banner" />
                <IosRingtonePermissionBanner medications={allMeds} onOpenAlertSettings={() => navigate('/settings')} />
                <Box>
                    <HomeDiscovery medications={allMeds} familySummary={familySummary} unreadReplies={unreadReviewReplies}
                        topSpacing={0} onOpenReview={() => { setReviewMode('default'); setReviewOpen(true); }} />
                </Box>
            </Box>

            {/* Mini Week Calendar */}
            {showMiniCalendar && (
                <Box sx={{ px: 1, pt: 0.25, pb: 2 }}>
                    <Box sx={{
                        display: 'flex',
                        overflowX: 'auto',
                        bgcolor: '#f9fafb',
                        borderRadius: 3,
                        p: 0.5,
                        gap: 0,
                        WebkitOverflowScrolling: 'touch',
                        scrollbarWidth: 'none',
                        touchAction: 'pan-x pan-y',
                        '&::-webkit-scrollbar': { display: 'none' },
                    }}
                        ref={handleMiniCalendarRef}
                    >
                        {(() => {
                            const days = [];
                            const dayLabels = [translationKey('일'), translationKey('월'), translationKey('화'), translationKey('수'), translationKey('목'), translationKey('금'), translationKey('토')].map(t);
                            const today = todayStr();
                            miniCalendarDates.forEach((dateStr) => {
                                const d = dateFromStr(dateStr);
                                const status = weekData[dateStr];
                                const isToday = dateStr === today;
                                const isFuture = dateStr > today;
                                const isSelected = selectedDate === dateStr || (!selectedDate && isToday);

                                let bgColor = 'transparent';
                                if (status === 'ALL_TAKEN') bgColor = '#bbf7d0';
                                else if (status === 'PARTIAL') bgColor = '#fde68a';
                                else if (status === 'MISSED') bgColor = '#fecaca';

                                days.push(
                                    <Box
                                        key={dateStr}
                                        data-date={dateStr}
                                        data-text-boundary={language !== 'ko' ? true : undefined}
                                        role="button"
                                        tabIndex={0}
                                        onClick={() => handleDateClick(dateStr)}
                                        sx={{
                                            flex: '0 0 calc(100% / 7)',
                                            minWidth: '2.6rem',
                                            display: 'flex', flexDirection: 'column', alignItems: 'center',
                                            py: 1, borderRadius: 2.5, cursor: 'pointer',
                                            bgcolor: isSelected ? '#fff' : 'transparent',
                                            boxShadow: isSelected ? '0 2px 8px rgba(0,0,0,0.08)' : 'none',
                                            transition: 'all 0.15s ease',
                                            '&:active': { transform: 'scale(0.94)' },
                                        }}
                                    >
                                        <CalendarDayText sx={{
                                            fontSize: '1.1rem', mb: 0.5, whiteSpace: 'nowrap',
                                            color: isToday ? '#5a4a00' : isSelected ? '#5a4a00' : '#aaa',
                                            fontWeight: isToday || isSelected ? 700 : 400,
                                            ...(language !== 'ko' && { width: '100%', textAlign: 'center' }),
                                        }}>
                                            {isToday ? t('오늘') : t(dayLabels[d.getDay()])}
                                        </CalendarDayText>
                                        <Box sx={{
                                            width: 44, height: 44, borderRadius: '50%',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            bgcolor: bgColor || (isSelected ? '#ffe566' : 'transparent'),
                                        }}>
                                            <Typography sx={{
                                                fontSize: '1.05rem', fontWeight: 800,
                                                color: isSelected ? '#5a4a00'
                                                    : status === 'ALL_TAKEN' ? '#15803d'
                                                    : status === 'MISSED' ? '#dc2626'
                                                    : isFuture ? '#ccc' : '#444',
                                            }}>{d.getDate()}</Typography>
                                        </Box>
                                        {/* 상태 점 */}
                                        {status && !isSelected && (
                                            <Box sx={{
                                                width: 5, height: 5, borderRadius: '50%', mt: 0.5,
                                                bgcolor: status === 'ALL_TAKEN' ? '#22c55e'
                                                    : status === 'PARTIAL' ? '#f59e0b'
                                                    : status === 'MISSED' ? '#ef4444' : 'transparent',
                                            }} />
                                        )}
                                        {(!status || isSelected) && <Box sx={{ width: 5, height: 5, mt: 0.5 }} />}
                                    </Box>
                                );
                            });
                            return days;
                        })()}
                    </Box>
                </Box>
            )}

            {/* 재고 부족 배너 */}
            {!selectedDate && lowStockMeds.length > 0 && (
                <Box role="button" tabIndex={0} onClick={() => navigate('/settings/stock')} sx={{
                    mx: 2, mt: 1.5, mb: 1.5, px: 1.75, py: 1.5, borderRadius: '16px', cursor: 'pointer',
                    background: 'linear-gradient(135deg, #fff 0%, #fffaf5 100%)',
                    border: '2px solid #fed7aa',
                    boxShadow: '0 8px 24px rgba(249, 115, 22, 0.08)',
                    display: 'flex', alignItems: 'center', gap: 1.4,
                    transition: 'opacity 0.15s ease, transform 0.15s ease',
                    '&:active': { opacity: 0.75, transform: 'scale(0.99)' },
                }}>
                    <Box sx={{
                        width: 48, height: 48, borderRadius: '14px', flexShrink: 0,
                        bgcolor: '#ffedd5',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        position: 'relative',
                    }}>
                        <Inventory2Icon sx={{ fontSize: 25, color: '#f97316' }} />
                        <Box sx={{
                            position: 'absolute', top: -3, right: -3,
                            width: 16, height: 16, borderRadius: '50%',
                            bgcolor: '#ef4444', border: '2px solid #fff',
                        }} />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{
                            fontSize: 16, fontWeight: 800, color: '#1a1a1a',
                            lineHeight: 1.3, mb: 0.5, ml: 0.9,
                        }}>
                            {t(lowStockMeds.some(m => m.remainingStock <= 0) ? '재고를 채워주세요' : '재고를 확인해주세요')}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                            {lowStockMeds.slice(0, 2).map(m => (
                                <Box key={m.id} sx={{
                                    maxWidth: '100%',
                                    px: 1, py: 0.4, borderRadius: '999px',
                                    bgcolor: m.remainingStock <= 0 ? '#fef2f2' : '#fff7ed',
                                    border: `1px solid ${m.remainingStock <= 0 ? '#fee2e2' : '#ffedd5'}`,
                                }}>
                                    <Typography sx={{
                                        fontSize: 13, fontWeight: 700,
                                        color: m.remainingStock <= 0 ? '#dc2626' : '#c2410c',
                                        lineHeight: 1.35,
                                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                    }}>
                                        {m.name} · {getStockMessage(m, t)}
                                    </Typography>
                                </Box>
                            ))}
                            {lowStockMeds.length > 2 && (
                                <Box sx={{ px: 1, py: 0.4, borderRadius: '999px', bgcolor: '#f9fafb', border: '1px solid #f3f4f6' }}>
                                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#777', lineHeight: 1.35 }}>
                                        {t('외 {count}개', { count: lowStockMeds.length - 2 })}
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                    </Box>
                    <ChevronRightIcon sx={{ fontSize: 20, color: '#c7c7c7', flexShrink: 0 }} />
                </Box>
            )}

            {/* 선택한 날짜 — 과거: 로그, 미래: 예정 */}
            {selectedDate && (
                <Box sx={{ px: 2, pt: 2 }}>
                    {(() => {
                        const [,m,d] = selectedDate.split('-').map(Number);
                        const nowD = new Date();
                        const todayStr = `${nowD.getFullYear()}-${String(nowD.getMonth() + 1).padStart(2, '0')}-${String(nowD.getDate()).padStart(2, '0')}`;
                        const isPast = selectedDate < todayStr;
                        const backfillableMeds = isPast ? allMeds.filter(med =>
                            canBackfillOnPastDate(med, selectedDate)
                            && !selectedDateLogs.some(l => l.medicationId === med.id)
                        ) : [];

                        return (
                            <>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1.5 }}>
                                    <EventIcon sx={{ fontSize: 20, color: '#8a7000' }} />
                                    <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: '#333' }}>
                                        {new Intl.DateTimeFormat(language === 'ko' ? 'ko-KR' : language === 'ja' ? 'ja-JP' : 'en-US', { month: 'long', day: 'numeric' }).format(new Date(Number(selectedDate.slice(0, 4)), m - 1, d))} {t(isPast ? '복용 기록' : '복용 예정')}
                                    </Typography>
                                </Box>

                                {isPast ? (
                                    <>
                                    {selectedDateLogs.length === 0 && backfillableMeds.length === 0 && (
                                        <SingleLineText sx={{ color: '#777', fontSize: '1rem', textAlign: 'center', py: 4 }}>{t('이 날의 복용 기록이 없어요')}</SingleLineText>
                                    )}
                                    {(() => {
                                        // selectedDate도 TAKEN 그룹화. SKIPPED/MISSED는 단일.
                                        const items = [];
                                        const takenGroups = new Map();
                                        for (const log of selectedDateLogs) {
                                            const time = log.scheduledTime?.slice(0, 5) || '';
                                            if (log.status === 'TAKEN') {
                                                if (!takenGroups.has(time)) takenGroups.set(time, { type: 'taken-group', time, logs: [] });
                                                takenGroups.get(time).logs.push(log);
                                            } else {
                                                items.push({ type: 'single', time, log });
                                            }
                                        }
                                        items.push(...takenGroups.values());
                                        items.sort((a, b) => a.time.localeCompare(b.time));
                                        return items;
                                    })().map(item => {
                                        if (item.type === 'taken-group') {
                                            const uniqueConfirmed = Array.from(new Set(item.logs.map(l => formatConfirmedTime(l.confirmedAt)).filter(Boolean))).sort();
                                            const summaryTime = uniqueConfirmed.length > 0 ? uniqueConfirmed.join(', ') : t('시간 미기록');
                                            return (
                                                <DoneGroupCard
                                                    key={`taken-${item.time}`}
                                                    time={summaryTime}
                                                    count={item.logs.length}
                                                    scheduledTime={item.time}
                                                    meds={item.logs.map(l => ({
                                                        id: String(l.id),
                                                        name: l.medicationName,
                                                        takenAt: formatConfirmedTime(l.confirmedAt) || t('시간 미기록'),
                                                        imageUrl: l.imageUrl,
                                                        intakeForm: l.intakeForm,
                                                        guardianNickname: l.confirmedByGuardianNickname,
                                                    }))}
                                                    onEditOne={(id) => {
                                                        const log = item.logs.find(l => String(l.id) === id);
                                                        if (log) openEditTime(log);
                                                    }}
                                                    onEditAll={() => openEditTimeAll(item.logs, item.time)}
                                                    /* selectedDate(과거 날짜) 카드는 되돌리기 비활성 — onUndoAll 미전달 */
                                                />
                                            );
                                        }
                                        const log = item.log;
                                        return (
                                            <Box key={log.id} data-home-history-log={log.id} sx={{
                                                display: 'flex', alignItems: 'center', gap: 1.5,
                                                p: 1.5, mb: 0.8, borderRadius: 3,
                                                bgcolor: log.status === 'MISSED' ? '#fef8f8' : '#f9fafb',
                                                border: log.status === 'MISSED' ? '1px solid #fecaca' : '1px solid #f0f0f0',
                                            }}>
                                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                                        {log.status === 'MISSED' && <BlockIcon sx={{ fontSize: 20, color: '#ef4444', flexShrink: 0 }} />}
                                                        {log.status === 'SKIPPED' && <SkipNextIcon sx={{ fontSize: 20, color: '#9ca3af', flexShrink: 0 }} />}
                                                        <Typography sx={{
                                                            fontSize: '1.05rem', fontWeight: 700,
                                                            color: log.status === 'MISSED' ? '#1a1a1a' : '#333',
                                                            minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                                        }}>
                                                            {log.medicationName}
                                                        </Typography>
                                                    </Box>
                                                    <SingleLineText sx={{ fontSize: '0.875rem', color: '#888', mt: 0.25 }}>{localize("복용 예정 ")}{log.scheduledTime?.slice(0, 5)}
                                                        {log.status === 'MISSED' && localize(' · 미복용')}
                                                        {log.status === 'SKIPPED' && localize(' · 건너뜀')}
                                                    </SingleLineText>
                                                    <SkipReasonDetails log={log} />
                                                </Box>
                                                {(log.status === 'MISSED' || log.status === 'SKIPPED' || log.status === 'PENDING') && (
                                                    <Button
                                                        disabled={processingIds.has(log.id)}
                                                        onClick={() => openConfirmMissedTime(log)}
                                                        sx={{
                                                            flexShrink: 0,
                                                            bgcolor: '#ffe566',
                                                            '&:hover': { bgcolor: '#e8d040' },
                                                            color: '#5a4a00',
                                                            borderRadius: 2,
                                                            px: 1,
                                                            py: 0.5,
                                                            width: 100,
                                                            minWidth: 0,
                                                            minHeight: 44,
                                                            fontSize: '0.875rem',
                                                            fontWeight: 800,
                                                            whiteSpace: 'nowrap',
                                                            '&:disabled': { bgcolor: '#e5e7eb', color: '#999' },
                                                        }}
                                                    >
                                                        {processingIds.has(log.id) ? <LoadingLabel>{null}</LoadingLabel> : localize('복용으로 변경')}
                                                    </Button>
                                                )}
                                            </Box>
                                        );
                                    })}
                                    {backfillableMeds.length > 0 && (
                                        <Box sx={{ mt: selectedDateLogs.length > 0 ? 2.5 : 0 }}>
                                            <SingleLineText sx={{ fontSize: '1.05rem', fontWeight: 700, color: '#333', mb: 0.3 }}>{t('이 날 혹시 드셨나요?')}</SingleLineText>
                                            <SingleLineText sx={{ fontSize: '0.95rem', color: '#777', mb: 1.5 }}>{t('등록 전이라 기록이 없어요. 드셨다면 남겨주세요')}</SingleLineText>
                                            {backfillableMeds.map(med => {
                                                const bkey = `backfill-${med.id}`;
                                                const processing = processingIds.has(bkey);
                                                const labels = medActionLabels(med.intakeForm, t);
                                                return (
                                                    <Box key={med.id} sx={{
                                                        p: 1.5, mb: 1, borderRadius: 3,
                                                        bgcolor: '#fffbeb', border: '1px solid #fde68a',
                                                    }}>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                                                            <MedImage src={med.imageUrl} size={40} />
                                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                                <Typography sx={{ fontWeight: 700, fontSize: '1.1rem', color: '#1a1a1a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{med.name}</Typography>
                                                                <Typography sx={{ fontSize: '0.95rem', color: '#777' }}>{med.frequencyType === 'EVERY_N_DAYS' ? localize("{v0}일마다", { v0: med.intervalDays }) : localize('매일')} {(med.times || []).map(t => t.slice(0,5)).join(', ')}</Typography>
                                                            </Box>
                                                        </Box>
                                                        <Box sx={{ display: 'flex', gap: 1 }}>
                                                            <Button fullWidth disabled={processing} onClick={() => handleBackfill(med.id, 'TAKEN')}
                                                                sx={{ bgcolor: '#ffe566', '&:hover': { bgcolor: '#e8d040' }, color: '#5a4a00', borderRadius: 3, py: 1.2, fontSize: '1rem', fontWeight: 700, '&:disabled': { bgcolor: '#e5e7eb', color: '#999' } }}>
                                                                {labels.backfillDone}
                                                            </Button>
                                                            <Button fullWidth disabled={processing} onClick={() => handleBackfill(med.id, 'SKIPPED')}
                                                                sx={{ border: '1px solid #e5e7eb', borderRadius: 3, py: 1.2, color: '#777', fontWeight: 600, fontSize: '1rem' }}>{t('건너뛰었어요')}</Button>
                                                        </Box>
                                                    </Box>
                                                );
                                            })}
                                        </Box>
                                    )}
                                    </>
                                ) : (
                                    /* 미래 날짜 — 복용 예정 */
                                    futureMeds.length === 0 ? (
                                        <SingleLineText sx={{ color: '#777', fontSize: '1rem', textAlign: 'center', py: 4 }}>{t('이 날 복용할 약이 없어요')}</SingleLineText>
                                    ) : futureMeds.slice().sort((a, b) => {
                                        const ta = (a.times?.[0] || '').slice(0, 5);
                                        const tb = (b.times?.[0] || '').slice(0, 5);
                                        return ta.localeCompare(tb) || ((a.id ?? 0) - (b.id ?? 0));
                                    }).map(med => (
                                        <Box key={med.id} sx={{
                                            display: 'flex', alignItems: 'center', gap: 1.5,
                                            p: 1.5, mb: 0.8, borderRadius: 3,
                                            bgcolor: '#f9fafb', border: '1px solid #f0f0f0',
                                        }}>
                                            <MedImage src={med.imageUrl} size={40} />
                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                <Typography sx={{ fontWeight: 700, fontSize: '1.15rem', color: '#1a1a1a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{med.name}</Typography>
                                                <Typography sx={{ fontSize: '1.1rem', color: '#777' }}>{med.times.map(t => t.slice(0, 5)).join(', ')}</Typography>
                                            </Box>
                                        </Box>
                                    ))
                                )}
                            </>
                        );
                    })()}
                </Box>
            )}

            {/* 오늘 복용 로그 */}
            {!selectedDate && (
                <Box sx={{ px: 2, pt: 0 }}>
                    {/* 일반 약 기준 빈 상태 — PRN은 메뉴 위 별도 카드(PrnHomeCard)에서 다룸 */}
                    {(!sortedLogs || sortedLogs.length === 0) ? (
                        <Box sx={{ textAlign: 'center', pt: !hasMedications && registrationDraft ? 0 : 8, pb: !hasMedications && registrationDraft ? 0 : 3 }}>
                            {!hasMedications && registrationDraft ? draftCard : <>
                            {hasMedications ? <SentimentSatisfiedAltIcon sx={{ fontSize: 48, mb: 2, color: '#8a7000' }} /> : <MedicationIcon sx={{ fontSize: 48, mb: 2, color: '#8a7000' }} />}
                            <Typography sx={{ fontWeight: 700, fontSize: '1.2rem', color: '#1a1a1a', mb: 0.5 }}>
                                {t(hasMedications ? (data?.globalPausedUntil >= localDateKey() ? '전체 복용 중지 중이에요' : '오늘은 복용할 약이 없어요') : '등록된 약이 없어요')}
                            </Typography>
                            <Typography sx={{ color: '#777', mb: 2, fontSize: '1rem' }}>
                                {t(hasMedications ? '다음 일정까지 편히 쉬세요' : '약을 등록하면 정한 시간에 알려드려요')}
                            </Typography>
                            {hasMedications && (() => {
                                // 다음 복용 일정 계산 — 같은 시간 약 여러 개 수집
                                const now = new Date();
                                const activeMeds = allMeds.filter(m => !m.paused && m.active !== false);
                                const entries = []; // { date, med, time, label }

                                entries.push(...nextScheduledEntries(activeMeds, now, data?.globalPausedUntil));
                                if (entries.length === 0) return null;

                                // 가장 빠른 시간 찾기
                                entries.sort((a, b) => a.date - b.date);
                                const earliest = entries[0];
                                // 같은 시간의 약 모두 수집
                                const nextMeds = entries.filter(e => e.time === earliest.time && e.label === earliest.label);

                                return (
                                    <Box sx={{
                                        mt: 1, p: 2, borderRadius: 3, bgcolor: '#f9fafb',
                                        border: '1px solid #f0f0f0', textAlign: 'left',
                                    }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: nextMeds.length > 0 ? 1 : 0 }}>
                                            <AccessTimeIcon sx={{ color: '#8a7000', fontSize: 20 }} />
                                            <Typography sx={{ fontSize: '1.05rem', fontWeight: 700, color: '#1a1a1a' }}>
                                                {t('다음 복용:')} {/^\d+일 뒤$/.test(earliest.label)
                                                    ? t('{count}일 뒤', { count: earliest.label.match(/^\d+/)[0] })
                                                    : t(earliest.label)} {earliest.time}
                                            </Typography>
                                        </Box>
                                        {nextMeds.map((m, i) => (
                                            <Typography key={i} sx={{
                                                fontSize: '1rem', color: '#555',
                                                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                            }}>
                                                · {m.med}
                                            </Typography>
                                        ))}
                                    </Box>
                                );
                            })()}
                            {!hasMedications && (
                                <Button disabled={checkingMedicationLimit} variant="contained" startIcon={checkingMedicationLimit ? <LoadingLabel>{null}</LoadingLabel> : <AddIcon />} onClick={() => openMedicationAdd('/medications/new-bundle?mode=manual')}
                                    sx={{ bgcolor: '#ffe566', '&:hover': { bgcolor: '#e8d040' }, color: '#5a4a00', borderRadius: 3, px: 4, py: 1.5, fontSize: '1.1rem', fontWeight: 700 }}>{t('약 등록하기')}</Button>
                            )}
                            </>}
                        </Box>
                    ) : (
                        <>
                            {/* SKIPPED만 남아있으면 미완료 안내 */}
                            {actionableLogs.length === 0 && completedLogs.length > 0 && !completedLogs.every(l => l.status === 'TAKEN') && (
                                <Box sx={{
                                    display: 'flex', alignItems: 'center', gap: 1.5,
                                    p: 2, mb: 1.5, borderRadius: 3,
                                    bgcolor: '#fffbeb', border: '1px solid #fde68a',
                                }}>
                                    <WarningAmberIcon sx={{ fontSize: 24, color: '#d97706' }} />
                                    <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: '#92400e' }}>
                                        {t('오늘 {taken}/{total}개 완료했어요', { taken: completedLogs.filter(l => l.status === 'TAKEN').length, total: completedLogs.length })}
                                    </Typography>
                                </Box>
                            )}

                            {/* 처리 필요 — 크고 눈에 띄게 */}
                            {actionableGroups.length > 0 && (
                                <Box sx={{ mb: 2 }}>
                                    {actionableGroups.map((group) => {
                                        // 단일 약: 기존 카드 그대로
                                        if (group.logs.length === 1) {
                                            const log = group.logs[0];
                                            const labels = medActionLabels(log.intakeForm, t);
                                            const isOverdue = new Date(`${group.date}T${log.scheduledTime}`) <= now;
                                            const diffMin = isOverdue ? Math.max(0, Math.floor((now - new Date(`${group.date}T${log.scheduledTime}`)) / 60000)) : 0;
                                            return (
                                                <Box key={log.id} sx={{
                                                    p: 2, mb: 2, borderRadius: 4,
                                                    bgcolor: '#fff',
                                                    border: isOverdue ? '2px solid #fecaca' : '2px solid #fef3c7',
                                                    boxShadow: isOverdue ? '0 2px 12px rgba(239,68,68,0.1)' : '0 2px 8px rgba(0,0,0,0.05)',
                                                }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                                                        <MedImage src={log.imageUrl} size={40} />
                                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                                            <Typography sx={{ fontWeight: 700, fontSize: '1.15rem', color: '#1a1a1a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{log.medicationName}</Typography>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.3 }}>
                                                                <AccessTimeIcon sx={{ fontSize: 16, color: '#777' }} />
                                                                <Typography sx={{ fontSize: '1.1rem', color: '#1a1a1a', fontWeight: 600 }}>{group.date !== todayStr() ? `${group.date.slice(5).replace('-', '/')} ` : ''}{log.scheduledTime.slice(0, 5)}</Typography>
                                                            </Box>
                                                        </Box>
                                                        {log.deferredUntil && new Date(log.deferredUntil) > new Date() ? (
                                                            <Box sx={{ px: 1, py: 0.3, borderRadius: 2, bgcolor: '#fffbeb', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                                <AlarmIcon sx={{ fontSize: 15, color: '#8a7000' }} />
                                                                <Typography sx={{ fontSize: '1.05rem', fontWeight: 700, color: '#8a7000' }}>
                                                                    {t('{time} 알림 미룸', { time: log.deferredUntil.slice(11, 16) })}
                                                                </Typography>
                                                            </Box>
                                                        ) : isOverdue && (
                                                            <Box sx={{ px: 1, py: 0.3, borderRadius: 2, bgcolor: '#fef2f2' }}>
                                                                <Typography sx={{ fontSize: '1.05rem', fontWeight: 700, color: '#ef4444' }}>
                                                                    {diffMin >= 60 ? t('{hours}시간 {minutes}분 지남', { hours: Math.floor(diffMin / 60), minutes: diffMin % 60 }) : t('{minutes}분 지남', { minutes: diffMin })}
                                                                </Typography>
                                                            </Box>
                                                        )}
                                                    </Box>
                                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                                        <Button fullWidth variant="contained" disabled={processingIds.has(log.id)} onClick={() => handleConfirm(log.id)}
                                                            sx={{
                                                                bgcolor: '#ffe566', '&:hover': { bgcolor: '#e8d040' }, color: '#5a4a00',
                                                                borderRadius: 3, py: 1.8, fontSize: '1.1rem', fontWeight: 700,
                                                                boxShadow: '0 2px 8px rgba(230,168,0,0.3)',
                                                                '&:active': { transform: 'scale(0.98)' },
                                                                '&:disabled': { bgcolor: '#e5e7eb', color: '#999' },
                                                            }}>
                                                            {labels.doneButton}
                                                        </Button>
                                                        <Box sx={{ display: 'flex', gap: 1 }}>
                                                            <Button fullWidth disabled={processingIds.has(log.id)}
                                                                onClick={() => openSnoozeForLog(log)}
                                                                sx={{ border: '1px solid #fde68a', borderRadius: 3, py: 1.5, color: '#8a7000', fontWeight: 600, fontSize: '1.05rem', bgcolor: '#fffbeb', '&:active': { bgcolor: '#fff3c4' } }}>
                                                                {log.deferredUntil && new Date(log.deferredUntil) > new Date() ? t('미룬 시간 변경') : labels.later}
                                                            </Button>
                                                            <Button fullWidth disabled={processingIds.has(log.id)}
                                                                onClick={() => { setSkipSheet(log.id); setSkipReason(''); }}
                                                                sx={{ border: '1px solid #e5e7eb', borderRadius: 3, py: 1.5, color: '#777', fontWeight: 600, fontSize: '1.05rem', '&:active': { bgcolor: '#f3f4f6' } }}>{t('건너뛰기')}</Button>
                                                        </Box>
                                                    </Box>
                                                </Box>
                                            );
                                        }

                                        // 그룹 카드 (2개 이상 같은 시각) — 한 카드에 모음
                                        const logIds = group.logs.map(l => l.id);
                                        const labels = groupActionLabels(group.logs, t);
                                        const anyProcessing = group.logs.some(l => processingIds.has(l.id));
                                        const scheduledAt = new Date(`${group.date}T${group.time}`);
                                        const groupOverdue = !group.isDeferred && scheduledAt <= now;
                                        const groupDiffMin = groupOverdue ? Math.max(0, Math.floor((now - scheduledAt) / 60000)) : 0;
                                        return (
                                            <Box key={group.key} sx={{
                                                p: 2, mb: 2, borderRadius: 4,
                                                bgcolor: '#fff',
                                                border: groupOverdue ? '2px solid #fecaca' : '2px solid #fef3c7',
                                                boxShadow: groupOverdue ? '0 2px 12px rgba(239,68,68,0.1)' : '0 2px 8px rgba(0,0,0,0.05)',
                                            }}>
                                                {/* 헤더: 시각 + 개수 + 상태 뱃지 */}
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.2 }}>
                                                    <Box sx={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 0.6 }}>
                                                        <AccessTimeIcon sx={{ fontSize: 18, color: '#1a1a1a' }} />
                                                        <Typography sx={{ fontWeight: 800, fontSize: '1.15rem', color: '#1a1a1a' }}>
                                                            {group.date !== todayStr() ? `${group.date.slice(5).replace('-', '/')} ` : ''}{group.time}
                                                        </Typography>
                                                        <Typography sx={{ fontSize: '1.05rem', color: '#777' }}>
                                                            · {t('{count}개', { count: group.logs.length })}
                                                        </Typography>
                                                    </Box>
                                                    {group.isDeferred && (
                                                        <Box sx={{ px: 1, py: 0.3, borderRadius: 2, bgcolor: '#fffbeb', display: 'flex', alignItems: 'center', gap: 0.4, flexShrink: 0 }}>
                                                            <AlarmIcon sx={{ fontSize: 15, color: '#8a7000' }} />
                                                            <SingleLineText sx={{ fontSize: '0.95rem', fontWeight: 700, color: '#8a7000' }}>{t('알림 미룸')}</SingleLineText>
                                                        </Box>
                                                    )}
                                                    {!group.isDeferred && groupOverdue && (
                                                        <Box sx={{ px: 1, py: 0.3, borderRadius: 2, bgcolor: '#fef2f2', flexShrink: 0 }}>
                                                            <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, color: '#ef4444' }}>
                                                                {groupDiffMin >= 60 ? t('{hours}시간 {minutes}분 지남', { hours: Math.floor(groupDiffMin / 60), minutes: groupDiffMin % 60 }) : t('{minutes}분 지남', { minutes: groupDiffMin })}
                                                            </Typography>
                                                        </Box>
                                                    )}
                                                </Box>

                                                {/* 약 행 — 탭 시 BottomSheet (단일 처리) */}
                                                <Box sx={{ mb: 1.2 }}>
                                                    {group.logs.map((log, idx) => {
                                                        const processing = processingIds.has(log.id);
                                                        return (
                                                            <Box key={log.id} role="button"
                                                                onClick={() => { if (!processing) { setMedActionSheet(log.id); markMedRowHintSeen(); } }}
                                                                sx={{
                                                                    display: 'flex', alignItems: 'center', gap: 1.2,
                                                                    py: 1, px: 0.5,
                                                                    borderTop: idx === 0 ? 'none' : '1px solid #f3f4f6',
                                                                    cursor: processing ? 'default' : 'pointer',
                                                                    opacity: processing ? 0.5 : 1,
                                                                    '&:active': processing ? {} : { bgcolor: '#f9fafb' },
                                                                    transition: 'background-color 0.1s',
                                                                }}>
                                                                <MedImage src={log.imageUrl} size={32} />
                                                                <Typography sx={{
                                                                    flex: 1, fontWeight: 600, fontSize: '1.05rem', color: '#1a1a1a',
                                                                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                                                }}>
                                                                    {log.medicationName}
                                                                </Typography>
                                                                <ChevronRightIcon sx={{ fontSize: 20, color: '#bbb' }} />
                                                            </Box>
                                                        );
                                                    })}
                                                </Box>

                                                {/* 안내문 (학습 후 사라짐) */}
                                                {!hideMedRowHint && (
                                                    <SingleLineText sx={{ fontSize: '0.9rem', color: '#aaa', textAlign: 'center', mb: 1.2 }}>{t('약을 눌러 따로 처리할 수도 있어요')}</SingleLineText>
                                                )}

                                                {/* 그룹 액션 — 토스 스타일 primary + secondary 2개 */}
                                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                                    <Button fullWidth variant="contained" disabled={anyProcessing}
                                                        onClick={() => handleConfirmGroup(logIds)}
                                                        sx={{
                                                            bgcolor: '#ffe566', '&:hover': { bgcolor: '#e8d040' }, color: '#5a4a00',
                                                            borderRadius: 3, py: 1.8, fontSize: '1.1rem', fontWeight: 700,
                                                            boxShadow: '0 2px 8px rgba(230,168,0,0.3)',
                                                            '&:active': { transform: 'scale(0.98)' },
                                                            '&:disabled': { bgcolor: '#e5e7eb', color: '#999' },
                                                        }}>
                                                        {labels.doneButton}
                                                    </Button>
                                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                                        <Button fullWidth disabled={anyProcessing}
                                                            onClick={() => openSnoozeForGroup(logIds)}
                                                            sx={{ border: '1px solid #fde68a', borderRadius: 3, py: 1.5, color: '#8a7000', fontWeight: 600, fontSize: '1.05rem', bgcolor: '#fffbeb', '&:active': { bgcolor: '#fff3c4' } }}>
                                                            {labels.later}
                                                        </Button>
                                                        <Button fullWidth disabled={anyProcessing}
                                                            onClick={() => { setBulkSkipSheet(logIds); setSkipReason(''); }}
                                                            sx={{ border: '1px solid #e5e7eb', borderRadius: 3, py: 1.5, color: '#777', fontWeight: 600, fontSize: '1.05rem', '&:active': { bgcolor: '#f3f4f6' } }}>{t('건너뛰기')}</Button>
                                                    </Box>
                                                </Box>
                                            </Box>
                                        );
                                    })}
                                </Box>
                            )}

                            {/* 완료된 것들 — 컴팩트. PRN은 PrnHomeCard 안에서 별도로 노출 */}
                            {completedLogs.length > 0 && (
                                <Box>
                                    {actionableLogs.length > 0 && (
                                        <SingleLineText sx={{ fontSize: '1.05rem', color: '#888', mb: 1, fontWeight: 600 }}>{t('완료')}</SingleLineText>
                                    )}
                                    {completedItems.map((item) => {
                                        if (item.type === 'taken-group') {
                                            const uniqueConfirmed = Array.from(new Set(item.logs.map(l => formatConfirmedTime(l.confirmedAt)).filter(Boolean))).sort();
                                            const summaryTime = uniqueConfirmed.length > 0 ? uniqueConfirmed.join(', ') : t('시간 미기록');
                                            return (
                                                <DoneGroupCard
                                                    key={`taken-${item.logs[0].id}`}
                                                    time={summaryTime}
                                                    count={item.logs.length}
                                                    scheduledTime={item.time}
                                                    meds={item.logs.map(l => ({
                                                        id: String(l.id),
                                                        name: l.medicationName,
                                                        takenAt: formatConfirmedTime(l.confirmedAt) || t('시간 미기록'),
                                                        imageUrl: l.imageUrl,
                                                        intakeForm: l.intakeForm,
                                                        guardianNickname: l.confirmedByGuardianNickname,
                                                    }))}
                                                    onEditOne={(id) => {
                                                        const log = item.logs.find(l => String(l.id) === id);
                                                        if (log) openEditTime(log);
                                                    }}
                                                    onEditAll={() => openEditTimeAll(item.logs, item.time)}
                                                    onUndoAll={() => handleUnconfirmGroup(item.logs.map(l => l.id))}
                                                />
                                            );
                                        }
                                        const log = item.log;
                                        return (
                                            <Box key={log.id} data-home-history-log={log.id} sx={{
                                                display: 'flex', alignItems: 'center', gap: 1.5,
                                                p: 1.5, mb: 0.8, borderRadius: 3,
                                                bgcolor: '#f9fafb',
                                                border: '1px solid #f0f0f0',
                                            }}>
                                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                                        <SkipNextIcon sx={{ fontSize: 20, color: '#9ca3af', flexShrink: 0 }} />
                                                        <Typography sx={{
                                                            fontSize: '1.05rem', fontWeight: 700, color: '#333',
                                                            minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                                        }}>
                                                            {log.medicationName}
                                                        </Typography>
                                                    </Box>
                                                    <SingleLineText sx={{ fontSize: '0.875rem', color: '#888', mt: 0.25, ...(language !== 'ko' && { whiteSpace: 'normal', overflowWrap: 'anywhere' }) }}>{localize("건너뜀 ")}{formatConfirmedTime(log.confirmedAt) || log.scheduledTime.slice(0, 5)}{localize(" · 예정 ")}{log.scheduledTime.slice(0, 5)}
                                                    </SingleLineText>
                                                    <SkipReasonDetails log={log} />
                                                </Box>
                                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, width: 100, flexShrink: 0 }}>
                                                    <Button
                                                        disabled={processingIds.has(log.id)}
                                                        onClick={() => openConfirmMissedTime(log)}
                                                        sx={{
                                                            flexShrink: 0,
                                                            bgcolor: '#ffe566',
                                                            '&:hover': { bgcolor: '#e8d040' },
                                                            color: '#5a4a00',
                                                            borderRadius: 2,
                                                            px: 1,
                                                            py: 0.5,
                                                            minWidth: 0,
                                                            minHeight: 44,
                                                            fontSize: '0.875rem',
                                                            fontWeight: 800,
                                                            whiteSpace: 'nowrap',
                                                            '&:disabled': { bgcolor: '#e5e7eb', color: '#999' },
                                                        }}
                                                    >
                                                        {processingIds.has(log.id) ? <LoadingLabel>{null}</LoadingLabel> : localize('복용으로 변경')}
                                                    </Button>
                                                    <Button disabled={processingIds.has(log.id)} onClick={() => handleUnconfirm(log.id)} sx={{
                                                        px: 1, py: 0.5, borderRadius: 2, minWidth: 0, minHeight: 44,
                                                        fontSize: '0.875rem', fontWeight: 600, color: '#666', bgcolor: '#f9fafb',
                                                        border: '1px solid #e5e7eb',
                                                        '&:active': { opacity: 0.5, bgcolor: '#f3f4f6' },
                                                    }}>
                                                        {processingIds.has(log.id) ? <LoadingLabel>{null}</LoadingLabel> : localize('되돌리기')}
                                                    </Button>
                                                </Box>
                                            </Box>
                                        );
                                    })}
                                </Box>
                            )}
                        </>
                    )}
                </Box>
            )}

            {/* 오늘 등록한 약 중 시각이 지난 게 있으면: "오늘 혹시 드셨나요?" */}
            {!selectedDate && (() => {
                const nowD = new Date();
                const todayStr = `${nowD.getFullYear()}-${String(nowD.getMonth() + 1).padStart(2, '0')}-${String(nowD.getDate()).padStart(2, '0')}`;
                const nowMin = nowD.getHours() * 60 + nowD.getMinutes();
                const loggedKeys = new Set((data?.logs || []).map(l => `${l.medicationId}:${l.scheduledTime?.slice(0, 5)}`));
                const meds = allMeds.filter(med => {
                    if (med.paused || med.active === false || med.globalPausedUntil >= todayStr) return false;
                    if (med.frequencyType !== 'DAILY') return false;
                    if (med.startDate !== todayStr) return false;
                    return (med.times || []).some(t => {
                        const tm = t.slice(0, 5);
                        const [h, m] = tm.split(':').map(Number);
                        return (h * 60 + m) < nowMin && !loggedKeys.has(`${med.id}:${tm}`);
                    });
                });
                if (meds.length === 0) return null;
                return (
                    <Box sx={{ px: 2, pt: 1 }}>
                        <SingleLineText sx={{ fontSize: '1.05rem', fontWeight: 700, color: '#333', mb: 0.3, ml: 1.5 }}>{t('오늘 혹시 드셨나요?')}</SingleLineText>
                        <SingleLineText sx={{ fontSize: '0.95rem', color: '#777', mb: 1.5, ml: 1.5 }}>{t('복용 시각이 이미 지난 약이에요')}</SingleLineText>
                        {meds.map(med => {
                            const bkey = `backfill-${med.id}`;
                            const processing = processingIds.has(bkey);
                            const labels = medActionLabels(med.intakeForm, t);
                            return (
                                <Box key={med.id} sx={{
                                    p: 1.5, mb: 1, borderRadius: 3,
                                    bgcolor: '#fffbeb', border: '1px solid #fde68a',
                                }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                                        <MedImage src={med.imageUrl} size={40} />
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                            <Typography sx={{ fontWeight: 700, fontSize: '1.1rem', color: '#1a1a1a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{med.name}</Typography>
                                            <Typography sx={{ fontSize: '0.95rem', color: '#777' }}>{(med.times || []).filter(t => {
                                                const tm = t.slice(0, 5);
                                                const [h, m] = tm.split(':').map(Number);
                                                return (h * 60 + m) < nowMin && !loggedKeys.has(`${med.id}:${tm}`);
                                            }).map(t => t.slice(0, 5)).join(', ')}</Typography>
                                        </Box>
                                    </Box>
                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                        <Button fullWidth disabled={processing} onClick={() => handleBackfill(med.id, 'TAKEN', todayStr)}
                                            sx={{ bgcolor: '#ffe566', '&:hover': { bgcolor: '#e8d040' }, color: '#5a4a00', borderRadius: 3, py: 1.2, fontSize: '1rem', fontWeight: 700, '&:disabled': { bgcolor: '#e5e7eb', color: '#999' } }}>
                                            {labels.backfillDone}
                                        </Button>
                                        <Button fullWidth disabled={processing} onClick={() => handleBackfill(med.id, 'SKIPPED', todayStr)}
                                            sx={{ border: '1px solid #e5e7eb', borderRadius: 3, py: 1.2, color: '#777', fontWeight: 600, fontSize: '1rem' }}>{t('건너뛰었어요')}</Button>
                                    </Box>
                                </Box>
                            );
                        })}
                    </Box>
                );
            })()}

            {hasMedications && registrationDraft && <Box sx={{ mx: 2, my: 2 }}><RegistrationDraftCard compact
                onResume={() => openMedicationAdd('/medications/new-bundle?mode=manual', null, 'resume')}
                onNew={() => openMedicationAdd('/medications/new-bundle?mode=manual', null, 'new')} /></Box>}

            {/* PRN 확장 카드 — 등록된 PRN이 1개 이상일 때만 메뉴 위 풀너비로 노출.
                0개일 땐 일반 메뉴 슬롯에 "필요할 때 먹는 약 · 추가하기"가 들어감 (아래 분기). */}
            {(() => {
                const prnMeds = allMeds.filter(m => m.frequencyType === 'AS_NEEDED' && !m.paused && m.active !== false);
                if (prnMeds.length === 0) return null;
                return (
                    <PrnHomeCard
                        prnMeds={prnMeds}
                        todayLogs={prnTodayLogs}
                        onHeaderClick={() => navigate('/prn')}
                        onChipClick={(med) => setPrnLogSheet({
                            open: true, mode: 'create', med, logId: null, date: null, time: null,
                        })}
                        adding={checkingMedicationLimit}
                        onAddClick={() => openMedicationAdd('/medications/new-bundle?mode=manual&frequency=AS_NEEDED')}
                        onTodayLogEdit={(log) => {
                            const d = new Date(log.takenAt);
                            const pad = (n) => String(n).padStart(2, '0');
                            const dateStr = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
                            const timeStr = `${pad(d.getHours())}:${pad(d.getMinutes())}`;
                            setPrnLogSheet({
                                open: true, mode: 'edit',
                                med: { id: log.medicationId, name: log.medicationName, imageUrl: log.imageUrl },
                                logId: log.logId, date: dateStr, time: timeStr,
                            });
                        }}
                        onTodayLogDelete={(log) => setPrnDeleteConfirm({
                            open: true, logId: log.logId, medicationName: log.medicationName,
                        })}
                    />
                );
            })()}

            {/* 홈 메뉴 영역 — 기능 진입점.
                PRN은 등록된 약이 있으면 위쪽 PrnHomeCard로 빠지고, 0개일 때만 일반 메뉴 슬롯으로 노출. */}
            {(() => {
                const lowStockCount = allMeds.filter(isLowStock).length;
                const prnCount = allMeds.filter(m => m.frequencyType === 'AS_NEEDED' && !m.paused && m.active !== false).length;
                const familyConnected = familySummary?.state === 'B' || familySummary?.state === 'C' || familySummary?.state === 'W';
                const familyRoute = familyConnected ? (familySummary?.route || '/guardian') : '/family-care';
                const stockStatus = getStockStatusText(allMeds, t);
                const reviewMenuItem = {
                    icon: <RateReviewIcon />,
                    tileBg: unreadReviewReplies > 0 ? '#fee2e2' : '#f1f5f9',
                    iconColor: unreadReviewReplies > 0 ? '#dc2626' : '#64748b',
                    label: t('건의함'),
                    statusText: t(unreadReviewReplies > 0 ? '답변이 도착했어요' : '의견 보내기'),
                    statusColor: unreadReviewReplies > 0 ? '#dc2626' : undefined,
                    badgeText: unreadReviewReplies > 0 ? String(unreadReviewReplies) : null,
                    onClick: () => { setReviewMode('default'); setReviewOpen(true); },
                };
                const baseMenuItems = [
                    {
                        icon: <FamilyRestroomIcon />,
                        tileBg: '#fff5d6', iconColor: '#fbbf24',
                        label: t('가족 약 챙기기'),
                        statusText: t(familyConnected ? '챙기는 중' : '시작하기'),
                        onClick: () => navigate(familyRoute),
                    },
                    {
                        icon: <SmartToyIcon />,
                        tileBg: '#f3e8ff', iconColor: '#9333ea',
                        label: t('AI 상담'),
                        statusText: t('약 궁금증 물어보기'),
                        onClick: () => navigate('/chat'),
                    },
                    // PRN: 0개일 때만 메뉴 슬롯에 노출 (1개 이상이면 위쪽 PrnHomeCard로 대체)
                    ...(prnCount === 0 ? [{
                        icon: <TouchAppIcon />,
                        tileBg: '#e0f2fe', iconColor: '#38bdf8',
                        label: t('필요할 때 먹는 약'),
                        statusText: t('추가하기'),
                        onClick: () => navigate('/prn/onboarding'),
                    }] : []),
                    {
                        icon: <Inventory2Icon />,
                        tileBg: '#ffedd5', iconColor: '#f97316',
                        label: t('재고 관리'),
                        statusText: stockStatus,
                        statusColor: lowStockCount > 0 ? '#dc2626' : undefined,
                        onClick: () => navigate('/settings/stock'),
                    },
                    {
                        icon: <EditNoteIcon />,
                        tileBg: '#dcfce7', iconColor: '#22c55e',
                        label: t('증상 기록'),
                        statusText: t('증상 남기기'),
                        onClick: () => navigate('/symptoms'),
                    },
                ];
                const menuItems = unreadReviewReplies > 0
                    ? [reviewMenuItem, ...baseMenuItems]
                    : [...baseMenuItems, reviewMenuItem];
                return <HomeMenuList items={menuItems} />;
            })()}

            {/* 서비스 건의함 */}
            <ReviewSheet
                open={reviewOpen}
                mode={reviewMode}
                onClose={() => {
                    setReviewOpen(false);
                    setReviewMode('default');
                }}
                onSubmitSuccess={() => {
                    setReviewSuccessOpen(true);
                    refreshUnreadReviewReplies();
                }}
                onSubmitError={() => {
                    setReviewSuccessOpen(false);
                    setSnackbar({ open: true, message: t('건의 전송에 실패했어요. 다시 시도해주세요'), severity: 'error' });
                }}
                onUnreadChanged={setUnreadReviewReplies}
            />

            <StoreReviewSheet
                open={storeReview.open}
                source={storeReview.source}
                onClose={closeStoreReview}
                onReview={handleStoreReviewRequest}
            />

            <CenterToast
                open={reviewSuccessOpen}
                message={t('소중한 의견 감사합니다')}
                subMessage={t('검토 후 반영 여부를 답변드릴게요')}
                variant="brand"
                onClick={() => setReviewSuccessOpen(false)}
            />

            <SkipSheet
                open={!!skipSheet}
                logs={skipSheet ? (data?.logs || []).filter(log => log.id === skipSheet) : []}
                reason={skipReason}
                onReasonChange={setSkipReason}
                processing={processingIds.has(skipSheet)}
                onClose={() => { setSkipSheet(null); setSkipReason(''); }}
                onSubmit={handleSkip}
            />

            {/* 약 행 탭 시 단일 약 액션 BottomSheet */}
            <BottomSheet open={!!medActionSheet} onClose={() => setMedActionSheet(null)}>
                {(() => {
                    const log = medActionSheet ? data?.logs?.find(l => l.id === medActionSheet) : null;
                    if (!log) return null;
                    const isDeferred = !!(log.deferredUntil && new Date(log.deferredUntil) > new Date());
                    const labels = medActionLabels(log.intakeForm, t);
                    return (
                        <>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, mb: 0.5 }}>
                                <MedImage src={log.imageUrl} size={36} />
                                <Box sx={{ flex: 1 }}>
                                    <Typography sx={{ fontWeight: 700, fontSize: '1.15rem', color: '#1a1a1a' }}>
                                        {log.medicationName}
                                    </Typography>
                                    <Typography sx={{ fontSize: '0.95rem', color: '#888' }}>
                                        {log.scheduledTime?.slice(0, 5)}
                                    </Typography>
                                </Box>
                            </Box>
                            <SingleLineText sx={{ fontSize: '1rem', color: '#888', mb: sheetSpacing.action }}>{t('이 약만 따로 처리해요')}</SingleLineText>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                <Button fullWidth variant="contained" disabled={processingIds.has(log.id)}
                                    onClick={() => { const id = log.id; setMedActionSheet(null); handleConfirm(id); }}
                                    sx={{
                                        bgcolor: '#ffe566', '&:hover': { bgcolor: '#e8d040' }, color: '#5a4a00',
                                        borderRadius: 3, py: 1.6, fontSize: '1.1rem', fontWeight: 700,
                                        boxShadow: '0 2px 8px rgba(230,168,0,0.3)',
                                        '&:disabled': { bgcolor: '#e5e7eb', color: '#999' },
                                    }}>
                                    {labels.doneButton}
                                </Button>
                                <Button fullWidth disabled={processingIds.has(log.id)}
                                    onClick={() => {
                                        setMedActionSheet(null);
                                        openSnoozeForLog(log);
                                    }}
                                    sx={{ border: '1px solid #fde68a', borderRadius: 3, py: 1.5, color: '#8a7000', fontWeight: 600, fontSize: '1.05rem', bgcolor: '#fffbeb' }}>
                                    {isDeferred ? t('미룬 시간 변경') : labels.later}
                                </Button>
                                <Button fullWidth disabled={processingIds.has(log.id)}
                                    onClick={() => { const id = log.id; setMedActionSheet(null); setSkipSheet(id); setSkipReason(''); }}
                                    sx={{ border: '1px solid #e5e7eb', borderRadius: 3, py: 1.5, color: '#777', fontWeight: 600, fontSize: '1.05rem' }}>{t('건너뛰기')}</Button>
                            </Box>
                        </>
                    );
                })()}
            </BottomSheet>

            <SkipSheet
                open={!!bulkSkipSheet}
                bulk
                logs={bulkSkipSheet ? bulkSkipSheet.map(id => data?.logs?.find(log => log.id === id)).filter(Boolean) : []}
                reason={skipReason}
                onReasonChange={setSkipReason}
                processing={bulkSkipSheet?.some(id => processingIds.has(id))}
                onClose={() => { setBulkSkipSheet(null); setSkipReason(''); }}
                onSubmit={async () => {
                    try {
                        await handleSkipGroup(bulkSkipSheet || [], skipReason || null);
                        setBulkSkipSheet(null);
                        setSkipReason('');
                    } catch (_) {}
                }}
            />

            {/* 다시 알림 시트 — single(개별)/bulk(그룹 일괄) 통합 SnoozeSheet */}
            <SnoozeSheet
                open={snoozeSheet.open}
                medName={snoozeSheet.medName}
                medCount={snoozeSheet.medCount}
                targets={snoozeSheet.targets}
                now={new Date()}
                defaultDelayMin={30}
                onClose={() => setSnoozeSheet(s => ({ ...s, open: false }))}
                onConfirm={handleSnoozeConfirm}
            />

            {/* 복용 시간 수정 시트 — single(개별)/bulk(그룹 일괄) 통합 */}
            <EditTakenTimeSheet
                open={timeEditSheet.open}
                mode={timeEditSheet.mode}
                scheduledTime={timeEditSheet.scheduledTime}
                scheduledDate={timeEditSheet.scheduledDate}
                initialDate={timeEditSheet.initialDate}
                targets={timeEditSheet.targets}
                initialTime={timeEditSheet.initialTime}
                action={timeEditSheet.action === 'confirm' ? 'record' : 'edit'}
                loadConfirmationWindow={getConfirmationWindow}
                onClose={() => setTimeEditSheet(s => ({ ...s, open: false }))}
                onConfirm={(newTime, takenDate) => {
                    const ids = timeEditSheet.targets.map(t => t.id);
                    if (timeEditSheet.action === 'confirm') {
                        return handleConfirmMissedAt(ids, timeEditSheet.scheduledDate, newTime, takenDate);
                    }
                    return handleApplyTime(ids, timeEditSheet.scheduledDate, newTime, takenDate);
                }}
            />

            {/* PRN 기록/수정 시트 — HomePage 확장 카드에서 1탭으로 진입 */}
            <PrnLogSheet
                open={prnLogSheet.open}
                mode={prnLogSheet.mode}
                medication={prnLogSheet.med}
                initialDate={prnLogSheet.date}
                initialTime={prnLogSheet.time}
                onClose={() => setPrnLogSheet(s => ({ ...s, open: false }))}
                onConfirm={handlePrnSheetConfirm}
            />

            <Dialog open={!!alarmConfirmation.failure} onClose={alarmConfirmation.dismiss}
                PaperProps={{ sx: { borderRadius: 4, width: '100%', maxWidth: 340, mx: 3 } }}>
                <DialogTitle sx={{ fontSize: '1.15rem', fontWeight: 700 }}>{t('복용 처리를 확인해주세요')}</DialogTitle>
                <DialogContent>
                    <Typography>{alarmConfirmation.failure?.message || t('완료 여부를 확인하지 못했어요. 연결과 계정을 확인한 뒤 다시 시도해주세요.')}</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={alarmConfirmation.dismiss}>{t('닫기')}</Button>
                    <Button onClick={alarmConfirmation.retry}>{t('다시 시도')}</Button>
                </DialogActions>
            </Dialog>

            {/* PRN 기록 삭제 컨펌 모달 — 완료 카드 행 휴지통 버튼에서 진입.
                medicationName은 Dialog transition 끝난 뒤에 정리 (깜빡임 방지). */}
            <Dialog
                open={prnDeleteConfirm.open}
                onClose={() => { if (!prnDeleting) setPrnDeleteConfirm(s => ({ ...s, open: false })); }}
                TransitionProps={{ onExited: () => setPrnDeleteConfirm({ open: false, logId: null, medicationName: '' }) }}
                PaperProps={{ sx: { borderRadius: 4, width: '100%', maxWidth: 340, mx: 3 } }}
            >
                <DialogTitle sx={{ fontSize: '1.15rem', fontWeight: 700, pb: 0.5 }}>{t('기록을 삭제할까요?')}</DialogTitle>
                <DialogContent>
                    <Typography sx={{ fontSize: '1.05rem', color: '#777' }}>
                        {prnDeleteConfirm.medicationName
                            ? t('{name} 복용 기록을 삭제해요.', { name: prnDeleteConfirm.medicationName })
                            : t('복용 기록을 삭제해요.')}
                    </Typography>
                    <SingleLineText sx={{ fontSize: '1.05rem', color: '#777', mt: 0.5 }}>{t('삭제하면 되돌릴 수 없어요.')}</SingleLineText>
                </DialogContent>
                <DialogActions sx={{ px: 2, pb: 2, gap: 1 }}>
                    <Button
                        disabled={prnDeleting}
                        onClick={() => setPrnDeleteConfirm(s => ({ ...s, open: false }))}
                        variant="outlined"
                        sx={{ color: '#666', borderColor: '#e5e7eb', fontWeight: 600, fontSize: '1.05rem', borderRadius: 2, flex: 1 }}
                    >{t('아니요')}</Button>
                    <Button
                        disabled={prnDeleting}
                        onClick={performPrnDelete}
                        variant="contained"
                        sx={{ bgcolor: '#ef4444', color: '#fff', fontWeight: 700, fontSize: '1.05rem', borderRadius: 2, flex: 1, '&:hover': { bgcolor: '#dc2626' } }}
                    >{prnDeleting ? <LoadingLabel /> : t('삭제할게요')}</Button>
                </DialogActions>
            </Dialog>

            <ScrollDownIndicator onVisibleChange={setScrollIndicatorVisible} />

            {hasMedications && !scrollIndicatorVisible && (
                <Fab disabled={checkingMedicationLimit} variant="extended" onClick={() => openMedicationAdd('/medications/new-bundle?mode=manual')}
                    sx={{
                        position: 'fixed', bottom: 80,
                        right: 'calc(50% - min(240px, 50vw) + 20px)',
                        bgcolor: '#ffe566', color: '#5a4a00',
                        '&:hover': { bgcolor: '#e8d040' },
                        boxShadow: '0 4px 12px rgba(230,168,0,0.4)',
                        fontWeight: 700, fontSize: '0.95rem', gap: 0.5,
                        borderRadius: '28px', px: 2.5,
                    }}>
                    <AddIcon sx={{ fontSize: 20 }} />
                    {checkingMedicationLimit ? <LoadingLabel /> : t('약 추가')}
                </Fab>
            )}

            <CenterToast
                open={centerToast.open}
                message={localize(centerToast.message)}
                subMessage={centerToast.subMessage}
                onClick={() => setCenterToast(prev => ({ ...prev, open: false }))}
            />

            <AppToast
                open={snackbar.open}
                autoHideDuration={snackbar.severity === 'error' ? 4000 : 3000}
                onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
                severity={snackbar.severity}
                message={localize(snackbar.message)}
            />
            <MedicationAddGateDialog />
        </Box>
    );
}
